import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import Linkify from 'react-linkify';
import { API, getAuthToken, removeAuthToken } from '../App';
import { BottomNav } from '../components/mobile/BottomNav';
import { PullToRefresh } from '../components/mobile/PullToRefresh';
import { InstallPrompt } from '../components/mobile/InstallPrompt';

// IST (Indian Standard Time) formatting utility - Kolkata/Chennai timezone
// Using 12-hour format with AM/PM
const formatIST = (dateString, formatStr = 'MMM dd, h:mm a') => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const options = { timeZone: 'Asia/Kolkata' };
    const istDate = new Date(date.toLocaleString('en-US', options));
    return format(istDate, formatStr);
  } catch (e) {
    return dateString;
  }
};
const formatISTDate = (dateString) => formatIST(dateString, 'MMM dd, yyyy');
const formatISTFull = (dateString) => formatIST(dateString, 'MMM dd, yyyy h:mm a');

// Format message content with clickable links, emails, and phone numbers
const formatMessageContent = (content) => {
  if (!content) return content;
  
  let formatted = content;
  
  // URL regex - matches http, https, www links
  const urlRegex = /(?<!href=["'])(?<!src=["'])(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
  
  // Email regex
  const emailRegex = /(?<!mailto:)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
  
  // Phone regex - matches various formats
  const phoneRegex = /(?<![\d-])(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}(?![\d-])/g;
  
  if (!/<a[^>]*>/.test(formatted)) {
    formatted = formatted.replace(urlRegex, (match) => {
      const href = match.startsWith('www.') ? `https://${match}` : match;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${match}</a>`;
    });
    
    formatted = formatted.replace(emailRegex, (match) => {
      return `<a href="mailto:${match}" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
    
    formatted = formatted.replace(phoneRegex, (match) => {
      const cleanNumber = match.replace(/[\s()-]/g, '');
      return `<a href="tel:${cleanNumber}" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
  }
  
  return formatted;
};

// Helper function to get proper image URL
const getImageUrl = (imagePath, apiBase) => {
  if (!imagePath || imagePath === '' || imagePath === 'null' || imagePath === 'undefined') return null;
  // Already a full URL or base64 data
  if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
    return imagePath;
  }
  // Path starting with /api - remove /api from base and add path
  if (imagePath.startsWith('/api/')) {
    return `${apiBase.replace('/api', '')}${imagePath}`;
  }
  // Path starting with /uploads
  if (imagePath.startsWith('/uploads')) {
    return `${apiBase.replace('/api', '')}${imagePath}`;
  }
  // Relative path - add to API base
  return `${apiBase}/${imagePath}`;
};

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getMascotById } from '../components/mascots/MascotCollection';
import EmojiPicker from '../components/EmojiPicker';
import LinkifyText from '../components/LinkifyText';
import { AdSlot } from '../components/ads/AdSlot';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Briefcase, Building2, MapPin, DollarSign, Link2, 
  Bell, User, FileText, Upload, CheckCircle2, Clock, Send,
  Linkedin, Globe, ChevronRight, X, MessageCircle, HelpCircle,
  Home, Building, Laptop, Search, Users, UserPlus, Share2, Award,
  BookOpen, ShoppingCart, LogOut, Settings, ChevronLeft, Menu,
  Edit, Save, Phone, GraduationCap, Eye, Headphones, Copy, Twitter, Star, Lightbulb,
  Gift, Wallet, TrendingUp, ExternalLink, Coins, CheckCircle, Smile, Loader2,
  Target, BarChart3, Route, Sparkles, Brain, ArrowRight, Play, Trophy, Zap, RefreshCw, Lock
} from 'lucide-react';

// Helper function to get gender emoji based on name
const getGenderEmoji = (name) => {
  if (!name) return '👤';
  const firstName = name.trim().split(' ')[0].toLowerCase();
  
  // Common female names (Indian + International)
  const femaleNames = [
    'priya', 'neha', 'anjali', 'pooja', 'riya', 'shreya', 'kavya', 'ananya', 'divya', 'aisha',
    'sakshi', 'tanvi', 'nisha', 'meera', 'aditi', 'sneha', 'swati', 'preeti', 'komal', 'mansi',
    'kritika', 'rashmi', 'sunita', 'radha', 'lakshmi', 'sarita', 'mamta', 'suman', 'geeta', 'rekha',
    'mary', 'sarah', 'emma', 'olivia', 'sophia', 'isabella', 'mia', 'charlotte', 'emily', 'jessica',
    'jennifer', 'amanda', 'ashley', 'stephanie', 'nicole', 'elizabeth', 'rachel', 'michelle', 'laura', 'anna',
    'fatima', 'ayesha', 'zara', 'sara', 'hina', 'sana', 'noor', 'amina', 'mariam', 'layla'
  ];
  
  // Common male names (Indian + International)
  const maleNames = [
    'rahul', 'amit', 'raj', 'vikram', 'arun', 'suresh', 'rajesh', 'vijay', 'sanjay', 'ajay',
    'rohit', 'nikhil', 'ankit', 'arjun', 'karan', 'varun', 'akash', 'gaurav', 'deepak', 'ashish',
    'vishal', 'manish', 'sachin', 'anil', 'ravi', 'prakash', 'mukesh', 'ramesh', 'dinesh', 'mahesh',
    'john', 'james', 'robert', 'michael', 'william', 'david', 'richard', 'joseph', 'thomas', 'charles',
    'daniel', 'matthew', 'anthony', 'mark', 'donald', 'steven', 'paul', 'andrew', 'joshua', 'kevin',
    'ahmed', 'mohammad', 'ali', 'omar', 'hassan', 'hussein', 'ibrahim', 'yusuf', 'khalid', 'tariq',
    'akhil', 'test'
  ];
  
  if (femaleNames.includes(firstName)) return '👩';
  if (maleNames.includes(firstName)) return '👨';
  return '👤'; // Default neutral
};

// Expandable Story Card Component
const ExpandableStoryCard = ({ story }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const testimonialText = story.testimonial || story.description || '';
  const isLongText = testimonialText.length > 200;

  return (
    <div 
      className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow"
    >
      {/* 5 Stars */}
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      
      {/* Quote Icon */}
      <div className="mb-4">
        <svg className="h-8 w-8 text-sky-200" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>
      </div>
      
      {/* Testimonial Text */}
      <div className="mb-6">
        <p className={`text-gray-700 ${!isExpanded && isLongText ? 'line-clamp-4' : ''}`}>
          {testimonialText}
        </p>
        {isLongText && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sky-600 hover:text-sky-700 text-sm font-medium mt-2 flex items-center gap-1"
          >
            {isExpanded ? (
              <>Show Less <ChevronLeft className="h-4 w-4" /></>
            ) : (
              <>Read More <ChevronRight className="h-4 w-4" /></>
            )}
          </button>
        )}
      </div>
      
      {/* Author Info */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center text-white font-bold">
          {story.name?.charAt(0)?.toUpperCase() || 'A'}
        </div>
        <div>
          <p className="font-semibold text-slate-800">{story.name}</p>
          <p className="text-sm text-gray-500">{story.location || story.position}</p>
        </div>
      </div>
    </div>
  );
};

// Define candidate dashboard sections and their corresponding tab permission IDs
const CANDIDATE_SECTION_PERMISSIONS = {
  'jobs': 'job-updates',           // Jobs section maps to job-updates tab permission
  'applications': 'all-applications',
  'refer-earn': 'refer-earn',      // Refer & Earn section
  'my-referrals': 'my-referrals',  // My Referrals section
  'companies': 'companies',
  'saved': 'saved-jobs',
  'support': 'support',
  'courses': 'courses'
};

// Helper function to check if user has access to a candidate dashboard section
const hasCandidateSectionAccess = (user, sectionId) => {
  // If no tab_permissions set, show all default sections
  if (!user?.tab_permissions || user.tab_permissions.length === 0) {
    return true;
  }
  
  // Map the section to its tab permission ID
  const permissionId = CANDIDATE_SECTION_PERMISSIONS[sectionId];
  if (!permissionId) {
    // For sections without mapping, allow access by default
    return true;
  }
  
  return user.tab_permissions.includes(permissionId);
};

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [jobUpdates, setJobUpdates] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
  const [applyingToJob, setApplyingToJob] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [activeSection, setActiveSection] = useState('jobs');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    current_company: '',
    current_position: '',
    experience_years: '',
    skills: '',
    education: '',
    linkedin_url: '',
    portfolio_url: '',
    location: '',
    bio: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // AI Resume Parser state
  const [parsingResume, setParsingResume] = useState(false);
  const [parsedResumeData, setParsedResumeData] = useState(null);
  const [showResumeParser, setShowResumeParser] = useState(false);
  const [editingSkills, setEditingSkills] = useState([]);
  
  // Interview Questions state
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState('all');
  
  const [applicationForm, setApplicationForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    aadhar_address: '',
    education: '',
    education_other: '',
    university_name: '',
    college_name: '',
    current_city_state: '',
    aadhar_phone_linked: '',
    total_experience: '',
    previous_company_name: '',
    previous_lpa: '',
    hear_about_us: '',
    friend_name: '',
    followed_instagram: '',
    resume_url: '',
    agrees_to_charges: false
  });
  
  // Daily application limit state
  const [dailyApplicationCount, setDailyApplicationCount] = useState({ daily_count: 0, limit: 2, remaining: 2 });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [lastAppliedJob, setLastAppliedJob] = useState(null);
  
  // Pull-to-refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Support ticket state
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [replyMessage, setReplyMessage] = useState('');
  const [supportTab, setSupportTab] = useState('tickets'); // 'tickets' or 'call-request'
  const [callRequestForm, setCallRequestForm] = useState({
    phone: '',
    preferred_date: '',
    preferred_time: '',
    reason: ''
  });
  const [isSubmittingCallRequest, setIsSubmittingCallRequest] = useState(false);

  // Phase 2 & 3: Job Match, Salary Insights, Assessments, Career Paths
  const [jobMatchScores, setJobMatchScores] = useState({}); // {job_id: match_data}
  const [salaryInsights, setSalaryInsights] = useState({}); // {job_id: salary_data}
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [isAssessmentActive, setIsAssessmentActive] = useState(false);
  const [assessmentStartTime, setAssessmentStartTime] = useState(null);
  const [assessmentTimeLeft, setAssessmentTimeLeft] = useState(null); // Timer countdown in seconds
  const [careerPaths, setCareerPaths] = useState([]);
  const [selectedCareerPath, setSelectedCareerPath] = useState(null);
  const [recommendedPaths, setRecommendedPaths] = useState([]);

  // Available time slots for call requests (10AM - 10PM, Mon-Sat; 10AM - 4PM on Sunday)
  const callTimeSlots = [
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '12:00 PM - 1:00 PM',
    '1:00 PM - 2:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM',
    '4:00 PM - 5:00 PM',
    '5:00 PM - 6:00 PM',
    '6:00 PM - 7:00 PM',
    '7:00 PM - 8:00 PM',
    '8:00 PM - 9:00 PM',
    '9:00 PM - 10:00 PM'
  ];

  // Sunday time slots (half day: 10AM - 4PM)
  const sundayTimeSlots = [
    '10:00 AM - 11:00 AM',
    '11:00 AM - 12:00 PM',
    '12:00 PM - 1:00 PM',
    '1:00 PM - 2:00 PM',
    '2:00 PM - 3:00 PM',
    '3:00 PM - 4:00 PM'
  ];

  // Check if a date is valid (all days are available now)
  const isValidDay = (dateStr) => {
    return true; // All days available - Mon-Sat full, Sunday half day
  };

  // Check if selected date is Sunday
  const isSunday = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return date.getDay() === 0;
  };

  // Get available time slots based on selected date
  const getAvailableTimeSlots = () => {
    if (isSunday(callRequestForm.preferred_date)) {
      return sundayTimeSlots;
    }
    return callTimeSlots;
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Handle call request submission
  const handleSubmitCallRequest = async (e) => {
    e.preventDefault();
    if (!callRequestForm.phone || !callRequestForm.preferred_date || !callRequestForm.preferred_time || !callRequestForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsSubmittingCallRequest(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets`, {
        subject: 'Call Request',
        message: callRequestForm.reason,
        ticket_type: 'call_request',
        phone: callRequestForm.phone,
        preferred_date: callRequestForm.preferred_date,
        preferred_time: callRequestForm.preferred_time
      }, config);
      toast.success('Call request submitted successfully! Our team will contact you during your preferred time slot.');
      setCallRequestForm({ phone: '', preferred_date: '', preferred_time: '', reason: '' });
      setSupportTab('tickets');
      fetchData(); // Refresh tickets
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit call request');
    } finally {
      setIsSubmittingCallRequest(false);
    }
  };
  
  // Share job function
  const handleShareJob = (job, platform) => {
    const jobUrl = `${window.location.origin}/job/${job.id}`;
    const jobTitle = `${job.position_name} at ${job.company_name}`;
    const jobText = `Check out this job: ${jobTitle}${job.location ? ` in ${job.location}` : ''}`;
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(jobText + '\n' + jobUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(jobText)}&url=${encodeURIComponent(jobUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`,
      copy: jobUrl
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(jobUrl);
      toast.success('Job link copied to clipboard!');
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };
  
  // Job filter state
  const [jobFilter, setJobFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [jobSearchQuery, setJobSearchQuery] = useState('');

  // Referral state
  const [isReferDialogOpen, setIsReferDialogOpen] = useState(false);
  const [referringToJob, setReferringToJob] = useState(null);
  const [myReferrals, setMyReferrals] = useState([]);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
  const [referralForm, setReferralForm] = useState({
    referred_name: '',
    referred_email: '',
    referred_phone: '',
    relationship: '',
    notes: ''
  });
  
  // Search suggestions state
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef(null);
  
  // Show all jobs state
  const [showAllJobs, setShowAllJobs] = useState(false);

  // Banner carousel state
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Success Stories state
  const [successStories, setSuccessStories] = useState([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [showAllStories, setShowAllStories] = useState(false);

  // Notification badge state for application status updates
  const [unreadStatusUpdates, setUnreadStatusUpdates] = useState(0);
  
  // Withdraw application dialog state
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawingApplicationId, setWithdrawingApplicationId] = useState(null);
  const [withdrawingAppName, setWithdrawingAppName] = useState('');
  
  // Notifications panel state
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const shownNotificationIds = React.useRef(new Set());

  // My Referral Wallet state
  const [myReferralWallet, setMyReferralWallet] = useState(null);
  const [myReferralEarnings, setMyReferralEarnings] = useState([]);
  const [isReferralSectionOpen, setIsReferralSectionOpen] = useState(false);
  const [copiedReferralCode, setCopiedReferralCode] = useState(false);
  const [referralFormLink, setReferralFormLink] = useState('');

  // Position Request state
  const [isPositionRequestOpen, setIsPositionRequestOpen] = useState(false);
  const [myPositionRequests, setMyPositionRequests] = useState([]);
  const [uploadingCV, setUploadingCV] = useState(false);
  const [positionRequestForm, setPositionRequestForm] = useState({
    candidate_name: '',
    company_name: '',
    position_name: '',
    company_link: '',
    cv_url: '',
    reason: '',
    notes: ''
  });

  // Footer config state
  const [footerConfig, setFooterConfig] = useState(null);

  // Popup announcement state
  const [activePopup, setActivePopup] = useState(null);
  const [showPopupModal, setShowPopupModal] = useState(false);
  const [dismissedPopupId, setDismissedPopupId] = useState(() => {
    return sessionStorage.getItem('dismissedPopupId') || null;
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Handle browser back button - prevent going to login page
  useEffect(() => {
    // Push a state to history so back button doesn't leave the app
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = (event) => {
      // When back button is pressed, push state again to stay on page
      window.history.pushState(null, '', window.location.href);
      
      // If any dialog is open, close it instead
      if (isApplyDialogOpen) {
        setIsApplyDialogOpen(false);
        return;
      }
      
      if (isReferDialogOpen) {
        setIsReferDialogOpen(false);
        return;
      }
      
      if (withdrawDialogOpen) {
        setWithdrawDialogOpen(false);
        return;
      }
      
      // Show a toast to indicate the app is still active
      toast.info('Press back again to stay in app', { duration: 1500 });
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isApplyDialogOpen, isReferDialogOpen, withdrawDialogOpen]);

  // Auto-slide banners
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  // Auto-slide success stories
  useEffect(() => {
    if (successStories.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentStoryIndex((prev) => (prev + 1) % successStories.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [successStories.length]);

  // Fetch interview questions when section is opened
  useEffect(() => {
    if (activeSection === 'questions' && interviewQuestions.length === 0) {
      fetchInterviewQuestions();
    }
    // Fetch assessments when section is opened
    if (activeSection === 'assessments' && assessments.length === 0) {
      fetchAssessments();
    }
    // Fetch career paths when section is opened
    if (activeSection === 'career-paths') {
      if (careerPaths.length === 0) fetchCareerPaths();
      fetchRecommendedPaths();
    }
  }, [activeSection]);

  // Assessment Timer Countdown
  useEffect(() => {
    if (!isAssessmentActive || assessmentTimeLeft === null) return;
    
    // Auto-submit when time runs out
    if (assessmentTimeLeft <= 0) {
      toast.warning('Time is up! Submitting your assessment...');
      submitAssessment();
      return;
    }
    
    const timer = setInterval(() => {
      setAssessmentTimeLeft(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isAssessmentActive, assessmentTimeLeft]);

  // Subscribe to push notifications
  const subscribeToPushNotifications = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push notifications not supported');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Push notification permission denied');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.vapid_public_key;
      
      if (!vapidPublicKey) {
        console.log('VAPID public key not available');
        return;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const token = getAuthToken();
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Push notification subscription successful');
    } catch (error) {
      console.error('Push notification subscription failed:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [userRes, jobsRes, applicationsRes, ticketsRes, companiesRes, referralsRes, bannersRes, storiesRes, footerRes, positionReqsRes, dailyCountRes, walletRes] = await Promise.all([
        axios.get(`${API}/auth/me`, config),
        axios.get(`${API}/job-updates`, config),
        axios.get(`${API}/job-applications`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/support-tickets/my`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/companies`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/candidate/referrals`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/banners/public/candidate`).catch(() => ({ data: [] })),
        axios.get(`${API}/success-stories/public`).catch(() => ({ data: [] })),
        axios.get(`${API}/footer-config`).catch(() => ({ data: null })),
        axios.get(`${API}/position-requests/my`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/job-applications/daily-count`, config).catch(() => ({ data: { daily_count: 0, limit: 2, remaining: 2 } })),
        axios.get(`${API}/referral/my-wallet`, config).catch(() => ({ data: { wallet: null, referrals: [] } }))
      ]);

      setCurrentUser(userRes.data);
      if (userRes.data?.role === 'candidate') localStorage.removeItem('officium_staff');
      
      // Subscribe to push notifications
      subscribeToPushNotifications();
      
      // Redirect non-candidates to appropriate dashboard
      if (userRes.data.role !== 'candidate') {
        if (userRes.data.role === 'lead_specialist') {
          navigate('/lead-specialist');
        } else {
          navigate('/');
        }
        return;
      }
      
      // Set initial active section based on user permissions
      // If user has restricted tab_permissions, set to the first allowed section
      const tabPerms = userRes.data.tab_permissions || [];
      if (tabPerms.length > 0) {
        // User has specific permissions set - find first allowed section
        const sectionOrder = ['jobs', 'applications', 'referrals', 'courses', 'support'];
        const firstAllowed = sectionOrder.find(section => {
          const permissionId = CANDIDATE_SECTION_PERMISSIONS[section];
          return permissionId && tabPerms.includes(permissionId);
        });
        if (firstAllowed) {
          setActiveSection(firstAllowed);
        }
      }
      
      setJobUpdates(jobsRes.data);
      setMyApplications(applicationsRes.data);
      setSupportTickets(ticketsRes.data);
      setCompanies(companiesRes.data);
      setMyReferrals(referralsRes.data);
      setDailyApplicationCount(dailyCountRes.data);
      setBanners(bannersRes.data);
      setSuccessStories(storiesRes.data);
      setFooterConfig(footerRes.data);
      setMyPositionRequests(positionReqsRes.data);
      
      // Set referral wallet data
      if (walletRes.data) {
        setMyReferralWallet(walletRes.data.wallet);
        setMyReferralEarnings(walletRes.data.referrals || []);
      }
      
      // Fetch referral form link
      try {
        const formLinkRes = await axios.get(`${API}/settings/referral-form-link`, config);
        setReferralFormLink(formLinkRes.data.link || '');
      } catch (err) {
        console.log('Referral form link not configured');
      }
      
      // Pre-fill position request form with user name
      if (userRes.data.name) {
        setPositionRequestForm(prev => ({ ...prev, candidate_name: userRes.data.name }));
      }
      
      // Check for unread status updates
      const lastSeenStatuses = JSON.parse(localStorage.getItem('lastSeenAppStatuses') || '{}');
      let unreadCount = 0;
      const newStatusMap = {};
      
      applicationsRes.data.forEach(app => {
        newStatusMap[app.id] = app.status;
        const lastSeen = lastSeenStatuses[app.id];
        // If we've seen this app before and status changed, count as unread
        if (lastSeen && lastSeen !== app.status) {
          unreadCount++;
        }
      });
      
      // Store current statuses for new applications (first time tracking)
      if (Object.keys(lastSeenStatuses).length === 0 && applicationsRes.data.length > 0) {
        localStorage.setItem('lastSeenAppStatuses', JSON.stringify(newStatusMap));
      }
      
      setUnreadStatusUpdates(unreadCount);
      
      // Fetch notifications
      fetchNotifications();
      
      // Pre-fill form with user data
      setApplicationForm(prev => ({
        ...prev,
        full_name: userRes.data.name || '',
        email: userRes.data.email || '',
        phone: userRes.data.phone || ''
      }));
    } catch (error) {
      if (error.response?.status === 401) {
        removeAuthToken();
        navigate('/login');
      }
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Pull-to-refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    toast.success('Jobs refreshed!');
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/notifications`, config);
      setNotifications(res.data || []);
    } catch (error) {
      console.debug('Failed to fetch notifications');
    }
  };

  // Fetch interview questions for candidates (with lock status)
  const fetchInterviewQuestions = async () => {
    try {
      setQuestionsLoading(true);
      const token = getAuthToken();
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/interview-questions/candidate/with-lock-status`, config);
      setInterviewQuestions(res.data || []);
    } catch (error) {
      console.debug('Failed to fetch interview questions');
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Phase 2: Fetch job match score for a specific job
  const fetchJobMatchScore = async (jobId) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/candidate/job-match/${jobId}`, config);
      setJobMatchScores(prev => ({ ...prev, [jobId]: res.data }));
    } catch (error) {
      console.debug('Failed to fetch job match score');
    }
  };

  // Phase 2: Fetch salary insights for a specific job
  const fetchSalaryInsights = async (jobId) => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/candidate/salary-insights/${jobId}`, config);
      setSalaryInsights(prev => ({ ...prev, [jobId]: res.data }));
    } catch (error) {
      console.debug('Failed to fetch salary insights');
    }
  };

  // Phase 3: Fetch skill assessments
  const fetchAssessments = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      // Use accessible endpoint to get assessments with lock status
      const res = await axios.get(`${API}/candidate/assessments/accessible`, config);
      setAssessments(res.data || []);
    } catch (error) {
      // Fallback to old endpoint if accessible endpoint doesn't exist
      try {
        const token = getAuthToken();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const res = await axios.get(`${API}/candidate/assessments`, config);
        setAssessments(res.data || []);
      } catch (e) {
        console.debug('Failed to fetch assessments');
      }
    }
  };

  // Phase 3: Fetch career paths
  const fetchCareerPaths = async () => {
    try {
      const res = await axios.get(`${API}/career-paths`);
      setCareerPaths(res.data || []);
    } catch (error) {
      console.debug('Failed to fetch career paths');
    }
  };

  // Phase 3: Fetch recommended career paths
  const fetchRecommendedPaths = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/candidate/recommended-career-path`, config);
      setRecommendedPaths(res.data.recommended_paths || []);
    } catch (error) {
      console.debug('Failed to fetch recommended paths');
    }
  };

  // Phase 3: Submit skill assessment
  const submitAssessment = async () => {
    if (!selectedAssessment) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const timeTaken = Math.floor((Date.now() - assessmentStartTime) / 1000);
      const answers = Object.entries(assessmentAnswers).map(([questionId, selectedAnswer]) => ({
        question_id: questionId,
        selected_answer: selectedAnswer
      }));
      
      const res = await axios.post(`${API}/candidate/assessments/${selectedAssessment.id}/submit`, {
        assessment_id: selectedAssessment.id,
        answers,
        time_taken_seconds: timeTaken
      }, config);
      
      toast.success(`Assessment completed! Score: ${res.data.score}%${res.data.passed ? ' - Passed!' : ''}`);
      setIsAssessmentActive(false);
      setSelectedAssessment(null);
      setAssessmentAnswers({});
      setAssessmentTimeLeft(null); // Reset timer
      fetchAssessments(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit assessment');
    }
  };

  // Check for recent notifications (real-time polling)
  const checkRecentNotifications = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/notifications/recent?seconds=10`, config);
      
      if (res.data.notifications && res.data.notifications.length > 0) {
        res.data.notifications.forEach(notif => {
          if (!shownNotificationIds.current.has(notif.id)) {
            shownNotificationIds.current.add(notif.id);
            
            // Show toast notification
            toast(notif.message, {
              icon: '🔔',
              duration: 5000,
              style: {
                background: '#EEF2FF',
                color: '#4338CA',
                border: '1px solid #C7D2FE'
              }
            });
          }
        });
        fetchNotifications();
      }
    } catch (error) {
      console.debug('Recent notifications check failed');
    }
  };

  // Real-time notification polling
  useEffect(() => {
    const interval = setInterval(checkRecentNotifications, 5000);
    return () => clearInterval(interval);
  }, []);

  // Play popup chime sound
  const playPopupSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create a pleasant chime sound
      const playNote = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Play a cheerful three-note chime
      const now = audioContext.currentTime;
      playNote(523.25, now, 0.15); // C5
      playNote(659.25, now + 0.1, 0.2); // E5
      playNote(783.99, now + 0.2, 0.25); // G5
    } catch (e) {
      // Audio not supported, silently fail
    }
  };

  // Fetch active popup announcement
  useEffect(() => {
    const fetchActivePopup = async () => {
      try {
        const response = await axios.get(`${API}/popup-announcement/active`);
        if (response.data && response.data.id !== dismissedPopupId) {
          setActivePopup(response.data);
          setShowPopupModal(true);
          // Play chime when popup appears
          setTimeout(() => playPopupSound(), 300);
        }
      } catch (error) {
        // No active popup or error, ignore
      }
    };
    fetchActivePopup();
  }, [dismissedPopupId]);

  const dismissPopup = () => {
    if (activePopup) {
      sessionStorage.setItem('dismissedPopupId', activePopup.id);
      setDismissedPopupId(activePopup.id);
    }
    setShowPopupModal(false);
  };

  // Mark application statuses as seen
  const markStatusesAsSeen = () => {
    const statusMap = {};
    myApplications.forEach(app => {
      statusMap[app.id] = app.status;
    });
    localStorage.setItem('lastSeenAppStatuses', JSON.stringify(statusMap));
    setUnreadStatusUpdates(0);
  };

  // Profile functions
  const openProfileEdit = () => {
    setProfileForm({
      name: currentUser?.name || '',
      phone: currentUser?.phone || '',
      current_company: currentUser?.current_company || '',
      current_position: currentUser?.current_position || '',
      experience_years: currentUser?.experience_years || '',
      skills: currentUser?.skills || '',
      education: currentUser?.education || '',
      linkedin_url: currentUser?.linkedin_url || '',
      portfolio_url: currentUser?.portfolio_url || '',
      location: currentUser?.location || '',
      bio: currentUser?.bio || ''
    });
    setIsEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.patch(`${API}/candidate/profile`, profileForm, config);
      
      // Update local state
      setCurrentUser(prev => ({ ...prev, ...profileForm }));
      setIsEditingProfile(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // AI Resume Parser functions
  const handleParseResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or DOCX file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }
    
    setParsingResume(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/candidate/resume/parse`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setParsedResumeData(response.data.parsed_data);
      setEditingSkills(response.data.parsed_data.skills || []);
      toast.success(`Resume parsed! Found ${response.data.skills_extracted} skills, ${response.data.experience_entries} experiences`);
      
      // Update local user state with new skills
      setCurrentUser(prev => ({ 
        ...prev, 
        skills: (response.data.parsed_data.skills || []).join(', '),
        resume_url: response.data.resume_url,
        resume_parsed: true
      }));
      
    } catch (error) {
      console.error('Resume parse error:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse resume');
    } finally {
      setParsingResume(false);
    }
  };
  
  const handleSaveSkills = async () => {
    try {
      const token = getAuthToken();
      await axios.put(`${API}/candidate/resume/skills`, editingSkills, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setCurrentUser(prev => ({ ...prev, skills: editingSkills.join(', ') }));
      toast.success('Skills saved!');
    } catch (error) {
      toast.error('Failed to save skills');
    }
  };
  
  const handleAddSkill = (skill) => {
    if (skill && !editingSkills.includes(skill)) {
      setEditingSkills([...editingSkills, skill]);
    }
  };
  
  const handleRemoveSkill = (skill) => {
    setEditingSkills(editingSkills.filter(s => s !== skill));
  };

  const handleApplyClick = (job) => {
    // Check if already applied
    const alreadyApplied = myApplications.some(app => app.job_id === job.id);
    if (alreadyApplied) {
      toast.error('You have already applied to this job');
      return;
    }
    
    // Check daily application limit
    if (dailyApplicationCount.remaining <= 0) {
      toast.error('You have reached the daily application limit. Please contact support for further assistance.');
      return;
    }
    
    setApplyingToJob(job);
    setIsApplyDialogOpen(true);
  };

  const handleWithdrawClick = (app) => {
    setWithdrawingApplicationId(app.id);
    setWithdrawingAppName(`${app.job_title} at ${app.company_name}`);
    setWithdrawDialogOpen(true);
  };

  const handleConfirmWithdraw = async () => {
    if (!withdrawingApplicationId) return;
    
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/job-applications/${withdrawingApplicationId}/withdraw`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setMyApplications(prev => prev.filter(app => app.id !== withdrawingApplicationId));
      toast.success('Application withdrawn successfully');
      setWithdrawDialogOpen(false);
      setWithdrawingApplicationId(null);
      setWithdrawingAppName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw application');
    }
  };

  const handleReferClick = (job) => {
    setReferringToJob(job);
    setReferralForm({
      referred_name: '',
      referred_email: '',
      referred_phone: '',
      relationship: '',
      notes: ''
    });
    setIsReferDialogOpen(true);
  };

  const handleSubmitReferral = async (e) => {
    e.preventDefault();
    if (!referralForm.referred_name || !referralForm.referred_email) {
      toast.error('Please fill in name and email');
      return;
    }

    try {
      const token = getAuthToken();
      await axios.post(`${API}/candidate/referrals`, {
        job_id: referringToJob.id,
        ...referralForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Referral submitted successfully!');
      setIsReferDialogOpen(false);
      setReferringToJob(null);
      fetchData(); // Refresh referrals
    } catch (error) {
      console.error('Referral error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit referral');
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingResume(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/job-applications/upload-resume`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setApplicationForm(prev => ({ ...prev, resume_url: response.data.resume_url }));
      toast.success('Resume uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload resume');
    } finally {
      setUploadingResume(false);
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    // Required field validation
    if (!applicationForm.full_name || !applicationForm.email || !applicationForm.phone) {
      toast.error('Please fill in Name, Email and Phone');
      return;
    }
    
    if (!applicationForm.aadhar_address) {
      toast.error('Please enter your address as per Aadhar card');
      return;
    }
    
    if (!applicationForm.current_city_state) {
      toast.error('Please enter your current city/location');
      return;
    }
    
    if (!applicationForm.education) {
      toast.error('Please select your education level');
      return;
    }
    
    if (!applicationForm.aadhar_phone_linked) {
      toast.error('Please indicate if your phone is linked with Aadhar');
      return;
    }
    
    if (!applicationForm.total_experience) {
      toast.error('Please select your total experience');
      return;
    }
    
    // If experienced (not fresher), require company name
    if (applicationForm.total_experience !== 'N/A' && !applicationForm.previous_company_name) {
      toast.error('Please enter your previous company name');
      return;
    }
    
    if (!applicationForm.hear_about_us) {
      toast.error('Please tell us how you heard about us');
      return;
    }
    
    if (applicationForm.hear_about_us === 'Friend' && !applicationForm.friend_name) {
      toast.error("Please enter your friend's name");
      return;
    }
    
    if (!applicationForm.followed_instagram) {
      toast.error('Please indicate if you follow us on Instagram');
      return;
    }
    
    if (!applicationForm.resume_url) {
      toast.error('Please upload your CV/Resume');
      return;
    }
    
    if (!applicationForm.agrees_to_charges) {
      toast.error('Please agree to the terms to proceed');
      return;
    }
    
    // Check daily limit one more time before submitting
    if (dailyApplicationCount.remaining <= 0) {
      toast.error('You have reached the daily application limit. Please contact support for further assistance.');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/job-applications`, {
        job_id: applyingToJob.id,
        ...applicationForm
      }, config);
      
      // Store the job info for success dialog
      setLastAppliedJob(applyingToJob);
      
      setIsApplyDialogOpen(false);
      setApplyingToJob(null);
      setApplicationForm({
        full_name: currentUser?.name || '',
        email: currentUser?.email || '',
        phone: currentUser?.phone || '',
        aadhar_address: '',
        education: '',
        education_other: '',
        university_name: '',
        college_name: '',
        current_city_state: '',
        aadhar_phone_linked: '',
        total_experience: '',
        previous_company_name: '',
        previous_lpa: '',
        hear_about_us: '',
        friend_name: '',
        followed_instagram: '',
        resume_url: '',
        agrees_to_charges: false
      });
      
      // Show success dialog with helpful tip
      setShowSuccessDialog(true);
      
      fetchData();
    } catch (error) {
      // Handle specific 429 rate limit error
      if (error.response?.status === 429) {
        toast.error('You have reached the daily application limit. Please contact support for further assistance.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit application');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Applied': return 'bg-blue-100 text-blue-700';
      case 'Called': return 'bg-yellow-100 text-yellow-700';
      case 'Shortlisted': return 'bg-purple-100 text-purple-700';
      case 'Emails Sent': return 'bg-emerald-100 text-emerald-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Support ticket functions
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets`, newTicket, config);
      toast.success('Support ticket created! Our team will respond soon.');
      setNewTicket({ subject: '', message: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const [isSendingReply, setIsSendingReply] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    if (selectedTicket?.replies?.length) {
      scrollToBottom();
    }
  }, [selectedTicket?.replies?.length]);
  
  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    if (isSendingReply) return; // Prevent double sending
    
    setIsSendingReply(true);
    const messageToSend = replyMessage.trim();
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    
    // Clear input IMMEDIATELY for instant feedback
    setReplyMessage('');
    
    // Optimistic update - add reply immediately with unique temp ID
    const optimisticReply = {
      id: tempId,
      message: messageToSend,
      sender_email: currentUser?.email,
      sender_name: currentUser?.name || 'You',
      created_at: new Date().toISOString(),
      is_sending: true,
      status: 'sending'
    };
    
    // Update selected ticket with optimistic reply
    setSelectedTicket(prev => ({
      ...prev,
      replies: [...(prev.replies || []), optimisticReply]
    }));
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/support-tickets/${selectedTicket.id}/reply`, { message: messageToSend }, config);
      
      // Mark message as sent (double tick)
      setSelectedTicket(prev => ({
        ...prev,
        replies: (prev.replies || []).map(r => 
          r.id === tempId ? { ...r, is_sending: false, status: 'sent' } : r
        )
      }));
      
      // Refresh tickets list in background (silently)
      const ticketsRes = await axios.get(`${API}/support-tickets/my`, config);
      setSupportTickets(ticketsRes.data);
      
      // Merge with real data but keep UI smooth
      const updatedTicket = ticketsRes.data.find(t => t.id === selectedTicket.id);
      if (updatedTicket) {
        // Only update if we have new messages from server
        setSelectedTicket(prev => {
          const serverReplies = updatedTicket.replies || [];
          const localOptimistic = (prev.replies || []).filter(r => r.id.startsWith('temp-'));
          // Keep only optimistic messages that aren't on server yet
          const pendingOptimistic = localOptimistic.filter(opt => 
            !serverReplies.some(sr => sr.message === opt.message && sr.sender_email === opt.sender_email)
          );
          return {
            ...updatedTicket,
            replies: [...serverReplies, ...pendingOptimistic]
          };
        });
      }
    } catch (error) {
      // Mark message as failed
      setSelectedTicket(prev => ({
        ...prev,
        replies: (prev.replies || []).map(r => 
          r.id === tempId ? { ...r, is_sending: false, status: 'failed' } : r
        )
      }));
      toast.error('Failed to send. Tap to retry.');
    } finally {
      setIsSendingReply(false);
    }
  };

  // Position Request Functions
  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('CV must be less than 5MB');
      return;
    }
    
    setUploadingCV(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = getAuthToken();
      const response = await axios.post(`${API}/upload/cv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      setPositionRequestForm(prev => ({ ...prev, cv_url: response.data.url }));
      toast.success('CV uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload CV');
    } finally {
      setUploadingCV(false);
    }
  };

  const handleSubmitPositionRequest = async (e) => {
    e.preventDefault();
    
    if (!positionRequestForm.candidate_name || !positionRequestForm.company_name || 
        !positionRequestForm.position_name || !positionRequestForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/position-requests`, positionRequestForm, config);
      
      toast.success('Position request submitted! You will receive an update via email within a week.');
      setIsPositionRequestOpen(false);
      setPositionRequestForm({
        candidate_name: currentUser?.name || '',
        company_name: '',
        position_name: '',
        company_link: '',
        cv_url: '',
        reason: '',
        notes: ''
      });
      
      // Refresh position requests
      const response = await axios.get(`${API}/position-requests/my`, config);
      setMyPositionRequests(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const getPositionRequestStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'In Review': return 'bg-blue-100 text-blue-700';
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      case 'Completed': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0F4C81] border-t-transparent mx-auto mb-4"></div>
          <p className="text-sm text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Sidebar Overlay */}
      {!sidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] transition-opacity"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Premium Side Navigation Drawer */}
      <aside className={`
        fixed top-0 left-0 h-full z-[70] bg-white
        transform transition-transform duration-300 ease-out
        ${sidebarCollapsed ? '-translate-x-full' : 'translate-x-0'}
        w-[280px] flex flex-col shadow-dropdown
      `}>
        {/* Header Section */}
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png?v=37" alt="Logo" className="h-14 w-auto object-contain" />
              <div>
                <h1 className="font-heading font-semibold text-[15px] text-slate-900 tracking-tight">Officium Connect</h1>
                <p className="text-[11px] text-slate-500 font-medium">Candidate Portal</p>
              </div>
            </div>
            <button 
              onClick={() => setSidebarCollapsed(true)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors group"
              aria-label="Close menu"
            >
              <X className="h-5 w-5 text-slate-400 group-hover:text-slate-600" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {hasCandidateSectionAccess(currentUser, 'jobs') && (
            <button
              onClick={() => { setActiveSection('jobs'); setSidebarCollapsed(true); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                activeSection === 'jobs' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
              data-testid="nav-jobs"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                activeSection === 'jobs' ? 'bg-white/15' : 'bg-slate-100'
              }`}>
                <Briefcase className={`h-[18px] w-[18px] ${activeSection === 'jobs' ? 'text-white' : 'text-slate-500'}`} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Jobs</span>
              {jobUpdates.length > 0 && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  activeSection === 'jobs' ? 'bg-white/20 text-white' : 'bg-[#10B981]/10 text-[#10B981]'
                }`}>
                  {jobUpdates.length}
                </span>
              )}
            </button>
            )}

            {hasCandidateSectionAccess(currentUser, 'applications') && (
            <button
              onClick={() => { setActiveSection('applications'); setSidebarCollapsed(true); markStatusesAsSeen(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                activeSection === 'applications' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
              data-testid="nav-applications"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                activeSection === 'applications' ? 'bg-white/15' : 'bg-slate-100'
              }`}>
                <FileText className={`h-[18px] w-[18px] ${activeSection === 'applications' ? 'text-white' : 'text-slate-500'}`} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">My Applications</span>
              {unreadStatusUpdates > 0 ? (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500 text-white">
                  {unreadStatusUpdates} new
                </span>
              ) : myApplications.length > 0 && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  activeSection === 'applications' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {myApplications.length}
                </span>
              )}
            </button>
            )}

            {/* Divider */}
            <div className="my-3 mx-2 border-t border-slate-100"></div>

            {/* Refer & Earn Button */}
            {hasCandidateSectionAccess(currentUser, 'refer-earn') && (
            <button
              onClick={() => { setActiveSection('refer-earn'); setSidebarCollapsed(true); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                activeSection === 'refer-earn' 
                  ? 'bg-[#10B981] text-white shadow-premium' 
                  : 'hover:bg-emerald-50 text-slate-700 border border-emerald-200'
              }`}
              data-testid="nav-refer-earn"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                activeSection === 'refer-earn' ? 'bg-white/15' : 'bg-emerald-50'
              }`}>
                <Gift className={`h-[18px] w-[18px] ${activeSection === 'refer-earn' ? 'text-white' : 'text-[#10B981]'}`} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Refer & Earn</span>
            </button>
            )}

            {/* My Referrals Button */}
            {hasCandidateSectionAccess(currentUser, 'my-referrals') && (
            <button
              onClick={() => { setActiveSection('my-referrals'); setSidebarCollapsed(true); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                activeSection === 'my-referrals' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
              data-testid="nav-my-referrals"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                activeSection === 'my-referrals' ? 'bg-white/15' : 'bg-slate-100'
              }`}>
                <Users className={`h-[18px] w-[18px] ${activeSection === 'my-referrals' ? 'text-white' : 'text-slate-500'}`} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">My Referrals</span>
              <div className="flex items-center gap-1.5">
                {myReferralWallet?.available_balance > 0 && (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                    activeSection === 'my-referrals' ? 'bg-[#10B981] text-white' : 'bg-[#10B981]/10 text-[#10B981]'
                  }`}>
                    ₹{myReferralWallet.available_balance}
                  </span>
                )}
                {myReferrals.length > 0 && (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                    activeSection === 'my-referrals' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {myReferrals.length}
                  </span>
                )}
              </div>
            </button>
            )}

            {/* Divider */}
            <div className="my-3 mx-2 border-t border-slate-100"></div>

            {/* Skill Assessments */}
            <button
              onClick={() => { setActiveSection('assessments'); setSidebarCollapsed(true); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                activeSection === 'assessments' 
                  ? 'bg-gradient-to-r from-[#0F4C81] to-[#10B981] text-white shadow-lg' 
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
              data-testid="nav-assessments"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                activeSection === 'assessments' ? 'bg-white/15' : 'bg-purple-50'
              }`}>
                <Brain className={`h-[18px] w-[18px] ${activeSection === 'assessments' ? 'text-white' : 'text-purple-500'}`} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Skill Assessments</span>
              {assessments.filter(a => a.completed && a.result?.passed).length > 0 && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  activeSection === 'assessments' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-600'
                }`}>
                  {assessments.filter(a => a.completed && a.result?.passed).length} verified
                </span>
              )}
            </button>

            {/* Career Paths */}
            <button
              onClick={() => { setActiveSection('career-paths'); setSidebarCollapsed(true); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                activeSection === 'career-paths' 
                  ? 'bg-gradient-to-r from-[#0F4C81] to-[#10B981] text-white shadow-lg' 
                  : 'hover:bg-slate-50 text-slate-700'
              }`}
              data-testid="nav-career-paths"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                activeSection === 'career-paths' ? 'bg-white/15' : 'bg-amber-50'
              }`}>
                <Route className={`h-[18px] w-[18px] ${activeSection === 'career-paths' ? 'text-white' : 'text-amber-500'}`} strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Career Paths</span>
            </button>

            <div className="my-3 mx-2 border-t border-slate-100"></div>

            {hasCandidateSectionAccess(currentUser, 'courses') && (
            <button
              onClick={() => { navigate('/courses'); setSidebarCollapsed(true); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-slate-50 text-slate-700"
              data-testid="nav-courses"
            >
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <BookOpen className="h-[18px] w-[18px] text-slate-500" strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Courses</span>
            </button>
            )}

            {hasCandidateSectionAccess(currentUser, 'support') && (
            <button
              onClick={() => { setIsSupportOpen(true); setSidebarCollapsed(true); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-slate-50 text-slate-700"
              data-testid="nav-support"
            >
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <HelpCircle className="h-[18px] w-[18px] text-slate-500" strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Support</span>
              {supportTickets.filter(t => t.status !== 'Closed').length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500 text-white">
                  {supportTickets.filter(t => t.status !== 'Closed').length}
                </span>
              )}
            </button>
            )}

            <button
              onClick={() => { setIsPositionRequestOpen(true); setSidebarCollapsed(true); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-slate-50 text-slate-700"
              data-testid="nav-position-request"
            >
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="h-[18px] w-[18px] text-slate-500" strokeWidth={1.5} />
              </div>
              <span className="font-medium text-[14px] flex-1 text-left">Request Position</span>
              {myPositionRequests.filter(r => r.status === 'Pending').length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                  {myPositionRequests.filter(r => r.status === 'Pending').length}
                </span>
              )}
            </button>
          </div>
        </nav>

        {/* Profile Section at Bottom */}
        <div className="p-4 border-t border-slate-100 space-y-2">
          {/* Profile Card */}
          <button
            onClick={() => { setIsProfileOpen(true); setSidebarCollapsed(true); }}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all duration-200 border border-slate-100"
            data-testid="sidebar-profile-btn"
          >
            <div className="w-10 h-10 rounded-full bg-[#0F4C81] flex items-center justify-center text-white font-heading font-semibold text-lg shadow-sm">
              {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-[14px] text-slate-800 truncate">{currentUser?.name}</p>
              <p className="text-[11px] text-slate-500">View Profile</p>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
          </button>

          {/* Logout Button */}
          <button
            onClick={() => {
              removeAuthToken();
              navigate('/');
              toast.success('Logged out successfully');
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 text-slate-600 hover:text-red-600 transition-all duration-200"
            data-testid="sidebar-logout-btn"
          >
            <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-red-100 flex items-center justify-center">
              <LogOut className="h-[18px] w-[18px] text-slate-500" strokeWidth={1.5} />
            </div>
            <span className="font-medium text-[14px]">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1">
        {/* Light Header - Matching Admin/Dashboard */}
        <header className="bg-gradient-to-r from-[#E8EDF4] to-[#F0F4F8] sticky top-0 z-30 shadow-sm border-b border-slate-200/50">
          <div className="px-4 py-1 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {/* Hamburger Menu Button */}
              <button 
                onClick={() => setSidebarCollapsed(false)}
                className="p-2 hover:bg-slate-200/50 rounded-lg transition-colors text-[#1e3a5f]"
                data-testid="hamburger-menu"
              >
                <Menu className="h-6 w-6" />
              </button>
              {/* Logo and Brand */}
              <img src="/logo.png?v=41" alt="Logo" className="h-14 md:h-16 w-auto" />
              <div className="hidden sm:block">
                <h2 className="text-lg font-heading font-semibold tracking-tight text-[#1e3a5f]">Officium Connect</h2>
              </div>
            </div>
            
            {/* Search Bar in Header with Suggestions */}
            <div className="flex-1 max-w-md mx-2 md:mx-4 relative" ref={searchRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={jobSearchQuery}
                  onChange={(e) => setJobSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-full text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200/50 focus:border-blue-300 text-sm transition-colors shadow-sm"
                  data-testid="header-search-input"
                />
              </div>
              
              {/* Search Suggestions Dropdown - Fixed for mobile */}
              {isSearchFocused && jobSearchQuery.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 max-h-[60vh] overflow-y-auto z-[100]" style={{ minWidth: '280px', width: '100%' }}>
                  {jobUpdates
                    .filter(job => {
                      const query = jobSearchQuery.toLowerCase();
                      return (
                        job.company_name?.toLowerCase().includes(query) ||
                        job.position_name?.toLowerCase().includes(query) ||
                        job.location?.toLowerCase().includes(query) ||
                        job.work_type?.toLowerCase().includes(query)
                      );
                    })
                    .slice(0, 5)
                    .map((job) => (
                      <div
                        key={job.id}
                        className="p-3 hover:bg-sky-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                        onClick={() => {
                          setSelectedJob(job);
                          setJobSearchQuery('');
                          setIsSearchFocused(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {job.company_logo_url ? (
                            <img 
                              src={getImageUrl(job.company_logo_url, API)}
                              alt={job.company_name}
                              className="h-8 w-8 md:h-10 md:w-10 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg bg-sky-100 flex items-center justify-center text-sky-600 font-bold flex-shrink-0 text-sm">
                              {job.company_name?.charAt(0)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <p className="font-medium text-gray-900 truncate text-sm">{job.position_name || 'Open Position'}</p>
                            <p className="text-xs text-gray-500 truncate">{job.company_name} • {job.location}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  
                  {jobUpdates.filter(job => {
                    const query = jobSearchQuery.toLowerCase();
                    return (
                      job.company_name?.toLowerCase().includes(query) ||
                      job.position_name?.toLowerCase().includes(query) ||
                      job.location?.toLowerCase().includes(query)
                    );
                  }).length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      <Search className="h-6 w-6 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No jobs found for "{jobSearchQuery}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Bell Notification Dropdown */}
              <DropdownMenu open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 hover:bg-slate-200/50 rounded-full transition-colors relative text-[#1e3a5f]"
                    data-testid="header-notifications-btn"
                    onClick={() => {
                      // Mark notifications as seen when bell is clicked
                      setTimeout(() => markStatusesAsSeen(), 500);
                    }}
                  >
                    <Bell className="h-5 w-5" />
                    {(unreadStatusUpdates + notifications.filter(n => !n.read).length) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                        {unreadStatusUpdates + notifications.filter(n => !n.read).length}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Bell className="h-4 w-4 text-sky-500" />
                      Notifications
                    </h3>
                  </div>
                  
                  {/* System Notifications */}
                  {notifications.filter(n => !n.read).length > 0 && (
                    <div className="p-2 border-b">
                      <p className="text-xs font-semibold text-slate-500 px-2 mb-2 uppercase">Recent</p>
                      {notifications.filter(n => !n.read).slice(0, 5).map((notif) => (
                        <div 
                          key={notif.id} 
                          className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer bg-indigo-50/50 flex items-start gap-2"
                        >
                          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                            <Bell className="h-3.5 w-3.5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700">{notif.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Application Status Updates */}
                  {myApplications.length > 0 && (
                    <div className="p-2">
                      <p className="text-xs font-semibold text-slate-500 px-2 mb-2 uppercase">Application Updates</p>
                      {myApplications.slice(0, 3).map((app) => (
                        <div 
                          key={app.id} 
                          className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                          onClick={() => {
                            setIsNotificationsOpen(false);
                            setActiveSection('applications');
                          }}
                        >
                          <p className="text-sm font-medium text-slate-700">{app.job_title}</p>
                          <p className="text-xs text-slate-500">{app.company_name}</p>
                          <Badge className={`text-[10px] mt-1 ${getStatusColor(app.status)}`}>{app.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* New Job Alerts */}
                  {jobUpdates.length > 0 && (
                    <div className="p-2 border-t">
                      <p className="text-xs font-semibold text-slate-500 px-2 mb-2 uppercase">New Jobs</p>
                      {jobUpdates.slice(0, 3).map((job) => (
                        <div 
                          key={job.id} 
                          className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                          onClick={() => {
                            setIsNotificationsOpen(false);
                            setSelectedJob(job);
                          }}
                        >
                          <p className="text-sm font-medium text-slate-700">{job.position_name || 'Open Position'}</p>
                          <p className="text-xs text-slate-500">{job.company_name}</p>
                          {job.salary_range && (
                            <span className="text-xs text-green-600 font-medium">{job.salary_currency || '₹'} {job.salary_range}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Referral Updates */}
                  {myReferrals.length > 0 && (
                    <div className="p-2 border-t">
                      <p className="text-xs font-semibold text-slate-500 px-2 mb-2 uppercase">Referral Updates</p>
                      {myReferrals.slice(0, 3).map((ref) => (
                        <div 
                          key={ref.id} 
                          className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                          onClick={() => {
                            setIsNotificationsOpen(false);
                            setActiveSection('referrals');
                          }}
                        >
                          <p className="text-sm font-medium text-slate-700">Referral: {ref.referred_name}</p>
                          <p className="text-xs text-slate-500">{ref.job_title}</p>
                          <Badge className={`text-[10px] mt-1 ${
                            ref.status === 'Approved' ? 'bg-green-100 text-green-700' :
                            ref.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{ref.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Empty State */}
                  {myApplications.length === 0 && jobUpdates.length === 0 && myReferrals.length === 0 && (
                    <div className="p-6 text-center">
                      <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No notifications yet</p>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <PullToRefresh onRefresh={fetchData}>
        <main className="p-4 md:p-6 pb-28 md:pb-6 space-y-6 bg-[#F8FAFC] min-h-screen">
          {/* Banner Carousel - Compact & Clickable */}
          {banners.filter(b => b.image_id || b.title).length > 0 && (
          <div className="relative w-full">
            <div className="relative aspect-[21/9] md:aspect-[21/6] rounded-xl overflow-hidden shadow-lg">
              {banners.filter(b => b.image_id || b.title).map((banner, index) => (
                <div
                  key={banner.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentBannerIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  {banner.link_url ? (
                    <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                      <img
                        src={banner.image_id ? `${API}/banners/image/${banner.image_id}` : 'https://placehold.co/1200x400/3b82f6/white?text=Banner'}
                        alt={banner.title || `Banner ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                      />
                      {banner.title && (
                        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent flex items-center pointer-events-none">
                          <div className="p-4 md:p-6 max-w-md">
                            <h3 className="text-white text-lg md:text-xl font-bold drop-shadow-lg line-clamp-1">
                              {banner.title}
                            </h3>
                          </div>
                        </div>
                      )}
                    </a>
                  ) : (
                    <>
                      <img
                        src={banner.image_id ? `${API}/banners/image/${banner.image_id}` : 'https://placehold.co/1200x400/3b82f6/white?text=Banner'}
                        alt={banner.title || `Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {banner.title && (
                        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent flex items-center">
                          <div className="p-4 md:p-6 max-w-md">
                            <h3 className="text-white text-lg md:text-xl font-bold drop-shadow-lg line-clamp-1">
                              {banner.title}
                            </h3>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Dots below banner */}
            {banners.filter(b => b.image_id || b.title).length > 1 && (
              <div className="flex justify-center gap-1.5 mt-2">
                {banners.filter(b => b.image_id || b.title).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBannerIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${index === currentBannerIndex ? 'bg-blue-600 w-4' : 'bg-gray-300'}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Jobs Section */}
        {activeSection === 'jobs' && (
          <>
        {/* Hero Section - Premium Design */}
        <div className="relative bg-white rounded-2xl shadow-card overflow-hidden mb-6">
          {/* Trust Banner */}
          <div className="bg-[#0F4C81] text-white py-8 px-6 md:px-10">
            <div className="max-w-3xl">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
                Find Verified Remote & Office Jobs Across India
              </h1>
              <p className="text-white/80 text-sm md:text-base mb-6 max-w-xl">
                Join thousands of professionals who found their dream jobs through our trusted platform. No scams, only verified opportunities.
              </p>
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => document.getElementById('jobs-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-6 py-2.5 bg-[#10B981] hover:bg-[#0D9668] text-white font-medium rounded-full transition-colors shadow-lg"
                  data-testid="explore-jobs-btn"
                >
                  Explore Jobs
                </button>
                <button 
                  onClick={() => setActiveSection('applications')}
                  className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full border border-white/30 transition-colors"
                  data-testid="my-applications-btn"
                >
                  My Applications
                </button>
              </div>
            </div>
          </div>
          
          {/* Trust Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-slate-50">
            <div className="text-center">
              <p className="font-heading text-2xl md:text-3xl font-semibold text-[#0F4C81]">1,000+</p>
              <p className="text-xs md:text-sm text-slate-600">Candidates Placed</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-2xl md:text-3xl font-semibold text-[#0F4C81]">10+</p>
              <p className="text-xs md:text-sm text-slate-600">Verified Companies</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-2xl md:text-3xl font-semibold text-[#10B981]">4.8</p>
              <p className="text-xs md:text-sm text-slate-600">User Rating</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-2xl md:text-3xl font-semibold text-[#10B981]">100%</p>
              <p className="text-xs md:text-sm text-slate-600">Secure Platform</p>
            </div>
          </div>
        </div>

        {/* Why Trust Us Section - Premium Cards */}
        <div className="bg-white rounded-2xl shadow-card p-6 md:p-8 mb-6">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 bg-[#0F4C81]/10 text-[#0F4C81] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
              Why Choose Us
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-slate-900 mb-2">
              Trusted by Thousands of Job Seekers
            </h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">
              We're committed to connecting talented professionals with verified employers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Our Vision */}
            <div className="group p-6 rounded-2xl border border-slate-200 hover:border-[#10B981]/30 hover:shadow-card-hover transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Eye className="h-6 w-6 text-[#10B981]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Our Vision</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Help every candidate find the right job based on their skills and qualifications, with a fair salary.
              </p>
            </div>

            {/* Our Mission */}
            <div className="group p-6 rounded-2xl border border-slate-200 hover:border-[#0F4C81]/30 hover:shadow-card-hover transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-[#0F4C81]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Briefcase className="h-6 w-6 text-[#0F4C81]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Our Mission</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Provide job opportunities for everyone, whether work from home or office, with comprehensive support.
              </p>
            </div>

            {/* Our Support */}
            <div className="group p-6 rounded-2xl border border-slate-200 hover:border-[#0F4C81]/30 hover:shadow-card-hover transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-[#0F4C81]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Headphones className="h-6 w-6 text-[#0F4C81]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Our Support</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                We guide you from the first interview to the final offer, ensuring you're fully prepared to succeed.
              </p>
            </div>
          </div>
        </div>

        {/* How to Get Started Section - Premium Design */}
        <div className="bg-white rounded-2xl shadow-card p-6 md:p-8 mb-6">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 bg-[#10B981]/10 text-[#10B981] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
              Simple Process
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-slate-900 mb-2">
              How to Get Started
            </h2>
            <p className="text-sm text-slate-500">
              Your journey to a successful career begins with just three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {/* Connecting Line (Desktop Only) */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-[#0F4C81]/20 via-[#10B981]/30 to-[#0F4C81]/20 z-0"></div>

            {/* Step 1 */}
            <div className="relative z-10">
              <div className="bg-slate-50 rounded-2xl p-6 text-center border border-slate-200 hover:border-[#0F4C81]/30 hover:shadow-card-hover transition-all duration-300">
                <div className="relative mx-auto mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-[#0F4C81] flex items-center justify-center mx-auto shadow-premium">
                    <Search className="h-7 w-7 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center border-2 border-[#0F4C81]">
                    <span className="text-xs font-bold text-[#0F4C81]">1</span>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Explore Opportunities</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Browse job listings that match your skills, experience, and career goals.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative z-10">
              <div className="bg-slate-50 rounded-2xl p-6 text-center border border-slate-200 hover:border-[#10B981]/30 hover:shadow-card-hover transition-all duration-300">
                <div className="relative mx-auto mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-[#10B981] flex items-center justify-center mx-auto shadow-premium">
                    <Briefcase className="h-7 w-7 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center border-2 border-[#10B981]">
                    <span className="text-xs font-bold text-[#10B981]">2</span>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Apply for a Role</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Select the position that fits you best and submit your application in a few simple steps.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative z-10">
              <div className="bg-slate-50 rounded-2xl p-6 text-center border border-slate-200 hover:border-[#0F4C81]/30 hover:shadow-card-hover transition-all duration-300">
                <div className="relative mx-auto mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-[#0F4C81] flex items-center justify-center mx-auto shadow-premium">
                    <Users className="h-7 w-7 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center border-2 border-[#0F4C81]">
                    <span className="text-xs font-bold text-[#0F4C81]">3</span>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">Get Guided Support</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Our team will review your application and guide you through the next stages of the process.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Job Listings - Premium Design */}
        <div id="jobs-section">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2">
                <Briefcase className="h-6 w-6 text-[#0F4C81]" strokeWidth={1.5} />
                Available Job Opportunities
              </h2>
              <p className="text-sm text-slate-500 mt-1">Find your perfect role from verified employers</p>
            </div>
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 transition-all disabled:opacity-50"
              data-testid="refresh-jobs-btn"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
          
          {/* Job Filter Tabs - Premium Pill Style */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setJobFilter('all')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                jobFilter === 'all' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
              data-testid="filter-all-jobs"
            >
              <Briefcase className="h-4 w-4" strokeWidth={1.5} />
              <span>All Jobs</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${jobFilter === 'all' ? 'bg-white/20' : 'bg-slate-100'}`}>
                {jobUpdates.length}
              </span>
            </button>
            <button
              onClick={() => setJobFilter('Work from Home')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                jobFilter === 'Work from Home' 
                  ? 'bg-[#10B981] text-white shadow-premium' 
                  : 'bg-white text-slate-600 hover:bg-emerald-50 border border-slate-200'
              }`}
              data-testid="filter-remote-jobs"
            >
              <Home className="h-4 w-4" strokeWidth={1.5} />
              <span>Remote</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${jobFilter === 'Work from Home' ? 'bg-white/20' : 'bg-emerald-50 text-emerald-600'}`}>
                {jobUpdates.filter(j => j.work_type === 'Work from Home').length}
              </span>
            </button>
            <button
              onClick={() => setJobFilter('Work from Office')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                jobFilter === 'Work from Office' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'bg-white text-slate-600 hover:bg-blue-50 border border-slate-200'
              }`}
              data-testid="filter-office-jobs"
            >
              <Building className="h-4 w-4" strokeWidth={1.5} />
              <span>Office</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${jobFilter === 'Work from Office' ? 'bg-white/20' : 'bg-blue-50 text-blue-600'}`}>
                {jobUpdates.filter(j => j.work_type === 'Work from Office').length}
              </span>
            </button>
            <button
              onClick={() => setJobFilter('Hybrid')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                jobFilter === 'Hybrid' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'bg-white text-slate-600 hover:bg-violet-50 border border-slate-200'
              }`}
              data-testid="filter-hybrid-jobs"
            >
              <Laptop className="h-4 w-4" strokeWidth={1.5} />
              <span>Hybrid</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${jobFilter === 'Hybrid' ? 'bg-white/20' : 'bg-violet-50 text-violet-600'}`}>
                {jobUpdates.filter(j => j.work_type === 'Hybrid').length}
              </span>
            </button>
            <button
              onClick={() => setJobFilter('by-company')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                jobFilter === 'by-company' 
                  ? 'bg-[#0F4C81] text-white shadow-premium' 
                  : 'bg-white text-slate-600 hover:bg-amber-50 border border-slate-200'
              }`}
              data-testid="filter-by-company"
            >
              <Building2 className="h-4 w-4" strokeWidth={1.5} />
              <span>By Company</span>
            </button>
          </div>
          
          {jobUpdates.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <Briefcase className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg text-gray-600">No job openings available at the moment</p>
                <p className="text-sm text-gray-400 mt-1">Check back later for new opportunities</p>
              </CardContent>
            </Card>
          ) : jobFilter === 'by-company' ? (
            /* Group jobs by company */
            <div className="space-y-6">
              {companies.length > 0 ? (
                companies.map(company => {
                  const companyJobs = jobUpdates.filter(j => j.company_name === company.name);
                  if (companyJobs.length === 0) return null;
                  return (
                    <div key={company.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 border-b flex items-center gap-3">
                        {company.logo_url ? (
                          <img 
                            src={getImageUrl(company.logo_url, API)}
                            alt={company.name}
                            className="h-12 w-12 rounded-xl object-cover border"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-xl">
                            {company.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-lg">{company.name}</h3>
                          <p className="text-sm text-gray-500">{companyJobs.length} open position{companyJobs.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="p-4 grid gap-3 md:grid-cols-2">
                        {companyJobs.map(job => {
                          const alreadyApplied = myApplications.some(app => app.job_id === job.id);
                          return (
                            <div 
                              key={job.id}
                              onClick={() => setSelectedJob(job)}
                              className="p-3 border rounded-xl hover:bg-gray-50 cursor-pointer transition-all"
                            >
                              <p className="font-medium text-violet-700">{job.position_name || 'Open Position'}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {job.work_type && (
                                  <Badge variant="secondary" className={`text-xs ${
                                    job.work_type === 'Work from Home' ? 'bg-green-100 text-green-700' :
                                    job.work_type === 'Hybrid' ? 'bg-purple-100 text-purple-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {job.work_type}
                                  </Badge>
                                )}
                                {job.salary_range && <Badge variant="outline" className="text-xs">{job.salary_range}</Badge>}
                                {alreadyApplied && <Badge className="bg-emerald-500 text-xs">Applied</Badge>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                /* Fallback: Group by company name from jobs */
                [...new Set(jobUpdates.map(j => j.company_name))].map(companyName => {
                  const companyJobs = jobUpdates.filter(j => j.company_name === companyName);
                  return (
                    <div key={companyName} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 border-b flex items-center gap-3">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-xl">
                          {companyName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{companyName}</h3>
                          <p className="text-sm text-gray-500">{companyJobs.length} open position{companyJobs.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="p-4 grid gap-3 md:grid-cols-2">
                        {companyJobs.map(job => {
                          const alreadyApplied = myApplications.some(app => app.job_id === job.id);
                          return (
                            <div 
                              key={job.id}
                              onClick={() => setSelectedJob(job)}
                              className="p-3 border rounded-xl hover:bg-gray-50 cursor-pointer transition-all"
                            >
                              <p className="font-medium text-violet-700">{job.position_name || 'Open Position'}</p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {job.work_type && (
                                  <Badge variant="secondary" className={`text-xs ${
                                    job.work_type === 'Work from Home' ? 'bg-green-100 text-green-700' :
                                    job.work_type === 'Hybrid' ? 'bg-purple-100 text-purple-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {job.work_type}
                                  </Badge>
                                )}
                                {job.salary_range && <Badge variant="outline" className="text-xs">{job.salary_range}</Badge>}
                                {alreadyApplied && <Badge className="bg-emerald-500 text-xs">Applied</Badge>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {jobUpdates
                .filter(job => {
                  // Filter by work type
                  const matchesFilter = jobFilter === 'all' || job.work_type === jobFilter;
                  // Filter by search query
                  const searchLower = jobSearchQuery.toLowerCase();
                  const matchesSearch = !jobSearchQuery || 
                    (job.company_name?.toLowerCase().includes(searchLower)) ||
                    (job.position_name?.toLowerCase().includes(searchLower)) ||
                    (job.location?.toLowerCase().includes(searchLower)) ||
                    (job.job_description?.toLowerCase().includes(searchLower));
                  return matchesFilter && matchesSearch;
                })
                .slice(0, showAllJobs ? undefined : 6)
                .map((job) => {
                const alreadyApplied = myApplications.some(app => app.job_id === job.id);
                const postedDate = job.published_at ? new Date(job.published_at) : (job.created_at ? new Date(job.created_at) : null);
                const postedAgo = postedDate ? formatDistanceToNow(postedDate, { addSuffix: true }) : null;
                return (
                  <div 
                    key={job.id} 
                    className="group bg-white rounded-2xl border border-slate-200 hover:border-[#0F4C81]/30 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden cursor-pointer"
                    onClick={() => setSelectedJob(job)}
                    data-testid={`job-card-${job.id}`}
                  >
                    <div className="p-5">
                      {/* Header with Logo and Company */}
                      <div className="flex items-start gap-4 mb-4">
                        {job.company_logo_url ? (
                          <img 
                            src={`${API.replace('/api', '')}${job.company_logo_url}`}
                            alt={job.company_name}
                            className="h-14 w-14 rounded-xl object-cover border border-slate-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="h-14 w-14 rounded-xl bg-[#0F4C81] flex items-center justify-center text-white font-heading font-semibold text-xl flex-shrink-0">
                            {job.company_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading font-semibold text-base text-slate-900 truncate">
                              {job.company_name}
                            </h3>
                            {job.is_verified && (
                              <span className="flex-shrink-0 bg-[#10B981]/10 text-[#10B981] text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
                                <CheckCircle2 className="h-3 w-3" />
                                Verified
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#0F4C81] font-medium truncate">
                            {job.position_name || 'Open Position'}
                          </p>
                          {postedAgo && (
                            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                              <Clock className="h-3 w-3" strokeWidth={1.5} />
                              {postedAgo}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Info Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {job.location && (
                          <span className="inline-flex items-center gap-1 text-xs bg-slate-50 text-slate-600 px-2.5 py-1 rounded-md">
                            <MapPin className="h-3 w-3" strokeWidth={1.5} />
                            {job.location}
                          </span>
                        )}
                        {job.work_type && (
                          <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${
                            job.work_type === 'Work from Home' ? 'bg-emerald-50 text-emerald-700' :
                            job.work_type === 'Hybrid' ? 'bg-violet-50 text-violet-700' :
                            'bg-blue-50 text-blue-700'
                          }`}>
                            {job.work_type === 'Work from Home' ? <Home className="h-3 w-3" strokeWidth={1.5} /> :
                             job.work_type === 'Hybrid' ? <Laptop className="h-3 w-3" strokeWidth={1.5} /> :
                             <Building className="h-3 w-3" strokeWidth={1.5} />}
                            {job.work_type === 'Work from Home' ? 'Remote' : job.work_type}
                          </span>
                        )}
                        {job.salary_range && (
                          <span className="inline-flex items-center gap-1 text-xs bg-[#10B981]/10 text-[#10B981] px-2.5 py-1 rounded-md font-medium">
                            {job.salary_currency || '₹'} {job.salary_range}
                          </span>
                        )}
                        {/* Job Match Score Badge - Phase 2 */}
                        {job.required_skills && job.required_skills.length > 0 && currentUser?.skills && (
                          <button
                            onClick={(e) => { e.stopPropagation(); if (!jobMatchScores[job.id]) fetchJobMatchScore(job.id); }}
                            data-testid={`match-score-${job.id}`}
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium cursor-pointer transition-all ${
                              jobMatchScores[job.id] 
                                ? jobMatchScores[job.id].score >= 70 
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : jobMatchScores[job.id].score >= 40
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                                : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                            }`}
                          >
                            <Target className="h-3 w-3" strokeWidth={1.5} />
                            {jobMatchScores[job.id] 
                              ? `${jobMatchScores[job.id].score}% Match`
                              : 'Check Match'
                            }
                          </button>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-100">
                        {alreadyApplied ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#10B981]/10 text-[#10B981] px-4 py-2 rounded-full">
                            <CheckCircle2 className="h-4 w-4" />
                            Applied
                          </span>
                        ) : dailyApplicationCount.remaining <= 0 ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-red-50 text-red-600 px-4 py-2 rounded-full">
                            <Clock className="h-4 w-4" />
                            Limit Reached
                          </span>
                        ) : (
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleApplyClick(job); }}
                            className="inline-flex items-center gap-1.5 text-sm font-medium bg-[#10B981] hover:bg-[#0D9668] text-white px-4 py-2 rounded-full transition-colors"
                            data-testid={`apply-btn-${job.id}`}
                          >
                            <Send className="h-4 w-4" strokeWidth={1.5} />
                            Apply Now
                          </button>
                        )}
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedJob(job); }}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-[#0F4C81] px-3 py-2 rounded-full border border-slate-200 hover:border-[#0F4C81]/30 transition-colors"
                          data-testid={`view-btn-${job.id}`}
                        >
                          <Eye className="h-4 w-4" strokeWidth={1.5} />
                          Details
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleShareJob(job, 'copy'); }}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-600 hover:bg-violet-50 px-3 py-2 rounded-full transition-colors"
                          data-testid={`share-btn-${job.id}`}
                          title="Share this job"
                        >
                          <Share2 className="h-4 w-4" strokeWidth={1.5} />
                          Share
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleReferClick(job); }}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F4C81] hover:bg-[#0F4C81]/5 px-3 py-2 rounded-full transition-colors"
                          data-testid={`refer-btn-${job.id}`}
                        >
                          <UserPlus className="h-4 w-4" strokeWidth={1.5} />
                          Refer
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* View All Jobs Button */}
            {!showAllJobs && jobUpdates.filter(job => {
              const matchesFilter = jobFilter === 'all' || job.work_type === jobFilter;
              const searchLower = jobSearchQuery.toLowerCase();
              const matchesSearch = !jobSearchQuery || 
                (job.company_name?.toLowerCase().includes(searchLower)) ||
                (job.position_name?.toLowerCase().includes(searchLower)) ||
                (job.location?.toLowerCase().includes(searchLower));
              return matchesFilter && matchesSearch;
            }).length > 6 && (
              <div className="mt-6 text-center">
                <Button
                  onClick={() => setShowAllJobs(true)}
                  className="bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white px-8 py-2 rounded-full shadow-lg"
                  data-testid="view-all-jobs-btn"
                >
                  View All Jobs ({jobUpdates.filter(job => {
                    const matchesFilter = jobFilter === 'all' || job.work_type === jobFilter;
                    return matchesFilter;
                  }).length})
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
            
            {showAllJobs && (
              <div className="mt-6 text-center">
                <Button
                  onClick={() => setShowAllJobs(false)}
                  variant="outline"
                  className="px-8 py-2 rounded-full"
                  data-testid="show-less-jobs-btn"
                >
                  Show Less
                </Button>
              </div>
            )}
            </>
          )}
        </div>
        
        {/* Success Stories Section */}
        {successStories.length > 0 && (
          <div className="mt-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">
                Success Stories from India
              </h2>
              <p className="text-gray-500 text-lg">
                Real professionals who found their freedom.
              </p>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(showAllStories ? successStories : successStories.slice(0, 6)).map((story) => (
                <ExpandableStoryCard key={story.id} story={story} />
              ))}
            </div>
            
            {/* Read More / Show Less Button - Only show when more than 6 stories */}
            {successStories.length > 6 && (
              <div className="text-center mt-8">
                <Button
                  variant="outline"
                  onClick={() => setShowAllStories(!showAllStories)}
                  className="px-8 py-2 rounded-full border-2 border-sky-500 text-sky-600 hover:bg-sky-50 font-semibold"
                >
                  {showAllStories ? (
                    <>
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Show Less
                    </>
                  ) : (
                    <>
                      Read More ({successStories.length - 6} more)
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Footer Section */}
        {footerConfig && (
          <footer className="mt-16 bg-gray-50 border-t border-gray-200" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 py-12">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {/* Company Info */}
                <div>
                  <h3 className="font-bold text-xl text-slate-800 mb-3">{footerConfig.company_name || 'Officium Connect'}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {footerConfig.company_tagline || "India's trusted platform for verified careers - Remote & Office."}
                  </p>
                </div>

                {/* Company Links */}
                <div>
                  <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider mb-4">COMPANY</h4>
                  <ul className="space-y-3">
                    {(footerConfig.company_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                      <li key={idx}>
                        <a 
                          href={link.url} 
                          className="text-gray-500 hover:text-sky-600 transition-colors text-sm"
                          data-testid={`footer-company-link-${idx}`}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Support Links */}
                <div>
                  <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider mb-4">SUPPORT</h4>
                  <ul className="space-y-3">
                    {(footerConfig.support_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                      <li key={idx}>
                        <a 
                          href={link.url} 
                          className="text-gray-500 hover:text-sky-600 transition-colors text-sm"
                          data-testid={`footer-support-link-${idx}`}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Legal Links */}
                <div>
                  <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider mb-4">LEGAL</h4>
                  <ul className="space-y-3">
                    {(footerConfig.legal_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                      <li key={idx}>
                        <a 
                          href={link.url} 
                          className="text-gray-500 hover:text-sky-600 transition-colors text-sm"
                          data-testid={`footer-legal-link-${idx}`}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bottom Section */}
              <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                  <p className="text-gray-400 text-sm">{footerConfig.copyright_text || `© ${new Date().getFullYear()} Officium Connect. All rights reserved.`}</p>
                  <p className="text-gray-400 text-sm mt-1">{footerConfig.made_in_text || 'MADE WITH ❤️ IN INDIA'}</p>
                </div>

                {/* Social Icons */}
                <div className="flex gap-4">
                  {footerConfig.social_instagram && (
                    <a href={footerConfig.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-600 transition-colors" data-testid="footer-instagram">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  )}
                  {footerConfig.social_facebook && (
                    <a href={footerConfig.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-600 transition-colors" data-testid="footer-facebook">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                  )}
                  {footerConfig.social_linkedin && (
                    <a href={footerConfig.social_linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-600 transition-colors" data-testid="footer-linkedin">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </footer>
        )}
          </>
        )}

        {/* My Applications Section */}
        {activeSection === 'applications' && (
          <>
        {/* My Applications with Enhanced Transparent Tracking */}
        {myApplications.length > 0 ? (
          <div id="applications-section">
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="h-6 w-6" style={{ color: '#2563EB', stroke: '#2563EB' }} />
              My Applications
            </h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              {myApplications.map((app) => {
                const isRejected = app.status === 'Rejected';
                const isWithdrawn = app.status === 'Withdrawn';
                const isHired = app.status === 'Hired' || app.status === 'Offer Extended';
                const isInterviewScheduled = app.status === 'Interview Scheduled';
                
                // Enhanced status steps for transparent tracking
                const statusSteps = ['Applied', 'Under Review', 'Shortlisted', 'Interview Scheduled', 'Offer Extended', 'Hired'];
                const currentStepIndex = (isRejected || isWithdrawn) ? -1 : statusSteps.indexOf(app.status);
                // Fallback for legacy statuses
                const legacyStatusMap = { 'Called': 1, 'Emails Sent': 2 };
                const effectiveStepIndex = currentStepIndex >= 0 ? currentStepIndex : (legacyStatusMap[app.status] ?? 0);
                
                return (
                  <Card key={app.id} data-testid={`application-card-${app.id}`} className={`border-0 shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl ${
                    isRejected ? 'border-l-4 border-red-500' : 
                    isWithdrawn ? 'border-l-4 border-gray-400' :
                    isHired ? 'border-l-4 border-emerald-500' :
                    isInterviewScheduled ? 'border-l-4 border-blue-500' : ''
                  }`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-slate-800">{app.job_title}</h3>
                          <p className="text-sm text-gray-500">{app.company_name}</p>
                        </div>
                        <Badge className={`${
                          app.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                          app.status === 'Under Review' ? 'bg-amber-100 text-amber-700' :
                          app.status === 'Shortlisted' ? 'bg-purple-100 text-purple-700' :
                          app.status === 'Interview Scheduled' ? 'bg-sky-100 text-sky-700' :
                          app.status === 'Offer Extended' ? 'bg-emerald-100 text-emerald-700' :
                          app.status === 'Hired' ? 'bg-green-100 text-green-700' :
                          app.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                          app.status === 'Withdrawn' ? 'bg-gray-100 text-gray-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{app.status}</Badge>
                      </div>
                      
                      {/* Show rejection notice */}
                      {isRejected && (
                        <div data-testid="rejection-reason-banner" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 text-red-700">
                            <X className="h-5 w-5" />
                            <span className="font-medium">Application Not Selected</span>
                          </div>
                          <p className="text-sm text-red-600 mt-1">
                            {app.rejection_reason || "Unfortunately, your application was not selected for this position. Keep applying to other opportunities!"}
                          </p>
                        </div>
                      )}
                      
                      {/* Show withdrawn notice */}
                      {isWithdrawn && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-700">
                            <X className="h-5 w-5" />
                            <span className="font-medium">Application Withdrawn</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            You withdrew your application for this position.
                          </p>
                        </div>
                      )}
                      
                      {/* Show interview scheduled notice */}
                      {isInterviewScheduled && app.interview_date && (
                        <div data-testid="interview-date-banner" className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 text-sky-700">
                            <Clock className="h-5 w-5" />
                            <span className="font-medium">Interview Scheduled</span>
                          </div>
                          <p className="text-sm text-sky-600 mt-1">
                            Your interview is scheduled for: <strong>{formatISTFull(app.interview_date)}</strong>
                          </p>
                          {app.interview_notes && (
                            <p className="text-xs text-sky-500 mt-1">{app.interview_notes}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Show hired/offer notice */}
                      {isHired && (
                        <div data-testid="offer-banner" className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 text-emerald-700">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="font-medium">{app.status === 'Offer Extended' ? 'Offer Extended!' : 'Congratulations!'}</span>
                          </div>
                          <p className="text-sm text-emerald-600 mt-1">
                            {app.status === 'Offer Extended' 
                              ? "Great news! You have received a job offer. Please check your email for details."
                              : "You've been hired! Welcome aboard. Check your email for onboarding details."}
                          </p>
                        </div>
                      )}
                      
                      {/* Progress Timeline - only show if not rejected/withdrawn */}
                      {!isRejected && !isWithdrawn && (
                        <div data-testid="status-timeline" className="mb-4">
                          <div className="flex items-center justify-between relative">
                            {/* Progress bar background */}
                            <div className="absolute top-3 left-0 right-0 h-1 bg-gray-200 rounded-full" />
                            {/* Progress bar fill */}
                            <div 
                              className={`absolute top-3 left-0 h-1 rounded-full transition-all duration-500 ${
                                isHired ? 'bg-gradient-to-r from-emerald-500 to-green-500' :
                                isInterviewScheduled ? 'bg-gradient-to-r from-blue-500 to-sky-500' :
                                'bg-gradient-to-r from-violet-500 to-fuchsia-500'
                              }`}
                              style={{ width: `${effectiveStepIndex >= 0 ? ((effectiveStepIndex + 1) / statusSteps.length) * 100 : 0}%` }}
                            />
                            
                            {statusSteps.map((step, index) => {
                              const isActive = index <= effectiveStepIndex;
                              const isCurrent = index === effectiveStepIndex;
                              
                              return (
                                <div key={step} className="flex flex-col items-center relative z-10">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                                    isActive 
                                      ? isHired ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg' :
                                        isInterviewScheduled ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-lg' :
                                        'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg' 
                                      : 'bg-gray-200 text-gray-500'
                                  } ${isCurrent ? 'ring-4 ring-opacity-30 ' + (isHired ? 'ring-emerald-300' : isInterviewScheduled ? 'ring-blue-300' : 'ring-violet-200') : ''}`}>
                                    {isActive ? <CheckCircle2 className="h-3 w-3" /> : (index + 1)}
                                  </div>
                                  <span className={`text-[9px] mt-1 font-medium text-center leading-tight ${
                                    isActive 
                                      ? isHired ? 'text-emerald-600' : isInterviewScheduled ? 'text-blue-600' : 'text-violet-600' 
                                      : 'text-gray-400'
                                  }`}>
                                    {step === 'Interview Scheduled' ? 'Interview' : step === 'Offer Extended' ? 'Offer' : step}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Status History - Show last update if available */}
                      {app.status_history && app.status_history.length > 0 && (
                        <div className="text-xs text-gray-500 mb-2 bg-gray-50 rounded p-2">
                          <span className="font-medium">Last Update:</span> {app.status_history[app.status_history.length - 1]?.status} 
                          {app.status_history[app.status_history.length - 1]?.timestamp && (
                            <span className="ml-1">on {formatISTDate(app.status_history[app.status_history.length - 1]?.timestamp)}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Applied {formatISTDate(app.created_at)}
                        </span>
                        {/* Withdraw button - only show if not rejected/hired/withdrawn */}
                        {!['Rejected', 'Hired', 'Selected', 'Withdrawn', 'Offer Extended'].includes(app.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleWithdrawClick(app)}
                            className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                            data-testid={`withdraw-btn-${app.id}`}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Withdraw
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Applications Yet</h3>
              <p className="text-gray-500 mb-4">Start applying to jobs to track your applications here</p>
              <Button onClick={() => setActiveSection('jobs')} className="bg-blue-600 hover:bg-blue-700">
                Browse Jobs
              </Button>
            </CardContent>
          </Card>
        )}
          </>
        )}

        {/* My Referrals Section - Consolidated with Refer & Earn */}
        {(activeSection === 'referrals' || activeSection === 'refer-earn' || activeSection === 'my-referrals') && (
          <>
            {/* Section Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className="font-heading text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Gift className="h-6 w-6 text-emerald-600" />
                My Referrals
              </h2>
              {myReferralWallet?.available_balance > 0 && (
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-lg font-bold">
                  <Wallet className="h-4 w-4 mr-2" />
                  Balance: ₹{myReferralWallet.available_balance}
                </Badge>
              )}
            </div>

            {/* Wallet Summary Card - Compact */}
            <Card className="border-0 shadow-xl overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 mb-6">
              <CardContent className="p-4">
                <div className="text-white grid grid-cols-4 gap-3 text-center">
                  <div>
                    <p className="text-amber-100 text-xs">Available</p>
                    <p className="text-xl font-bold">₹{myReferralWallet?.available_balance || 0}</p>
                  </div>
                  <div>
                    <p className="text-amber-100 text-xs">Earned</p>
                    <p className="text-xl font-bold">₹{myReferralWallet?.total_earned || 0}</p>
                  </div>
                  <div>
                    <p className="text-amber-100 text-xs">Pending</p>
                    <p className="text-xl font-bold">₹{myReferralWallet?.pending_amount || 0}</p>
                  </div>
                  <div>
                    <p className="text-amber-100 text-xs">Withdrawn</p>
                    <p className="text-xl font-bold">₹{myReferralWallet?.total_withdrawn || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How Referral Works Banner */}
            <Card className="border-0 shadow-lg mb-6 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5" />
                  How to Refer & Earn Rewards
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs shrink-0">1</div>
                    <div>
                      <p className="font-medium text-sm">Share with Friends</p>
                      <p className="text-emerald-100 text-xs">Refer this app to friends & family</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs shrink-0">2</div>
                    <div>
                      <p className="font-medium text-sm">They Apply</p>
                      <p className="text-emerald-100 text-xs">Your friend downloads & applies for a job</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-yellow-300/30 flex items-center justify-center font-bold text-xs shrink-0">3</div>
                    <div>
                      <p className="font-medium text-sm">Clear Telephonic</p>
                      <p className="text-yellow-200 text-xs font-semibold">Pass screening → ₹250</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-300/30 flex items-center justify-center font-bold text-xs shrink-0">4</div>
                    <div>
                      <p className="font-medium text-sm">Registration Done</p>
                      <p className="text-green-200 text-xs font-semibold">Hired → Earn ₹500!</p>
                    </div>
                  </div>
                </div>
                
                {/* Mandatory Referral Form Link */}
                {referralFormLink && (
                  <div className="mt-4 p-3 bg-white/10 rounded-lg border border-white/20">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-sm flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Mandatory: Fill Referral Form
                        </p>
                        <p className="text-xs text-emerald-100 mt-1">You must fill this form to register your referral and earn rewards</p>
                      </div>
                      <a 
                        href={referralFormLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg font-medium text-sm hover:bg-emerald-50 transition-colors shrink-0"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Fill Form Now
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Tracking & Support Info */}
                <div className="mt-4 pt-4 border-t border-white/20 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-2 text-emerald-100 text-sm">
                    <Eye className="h-4 w-4" />
                    <span>Track all your referrals' progress in real-time below</span>
                  </div>
                  <Button 
                    variant="secondary"
                    size="sm"
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    onClick={() => {
                      setIsSupportOpen(true);
                      toast.info('You can ask about your referral status in support');
                    }}
                    data-testid="referral-support-btn"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Need Help?
                  </Button>
                </div>
              </CardContent>
            </Card>

        {myReferrals.length > 0 ? (
          <div id="referrals-section">
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Share2 className="h-6 w-6 text-green-600" />
              My Referrals ({myReferrals.length})
            </h2>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {myReferrals.map((referral) => {
                // Define progress steps
                const steps = ['Pending', 'Contacted', 'Applied', 'Telephonic Cleared', 'Registration Done', 'Hired'];
                const currentStepIndex = steps.indexOf(referral.status);
                const isRejected = referral.status === 'Rejected';
                const isRewarded = referral.reward_given;
                
                return (
                <Card key={referral.id} className={`border-0 shadow-lg overflow-hidden ${isRewarded ? 'ring-2 ring-green-400' : ''}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${
                          isRewarded ? 'bg-gradient-to-br from-green-400 to-emerald-600' :
                          isRejected ? 'bg-gradient-to-br from-red-400 to-rose-600' :
                          'bg-gradient-to-br from-blue-400 to-indigo-600'
                        }`}>
                          {isRewarded ? <CheckCircle className="h-5 w-5" /> : referral.referred_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{referral.referred_name}</h3>
                          <p className="text-xs text-gray-500">{referral.referred_email}</p>
                        </div>
                      </div>
                      {isRewarded ? (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                          <Gift className="h-3 w-3" />
                          ₹{referral.reward_amount || 500} Earned
                        </Badge>
                      ) : (
                        <Badge className={
                          referral.status === 'Hired' ? 'bg-green-100 text-green-700' :
                          referral.status === 'Registration Done' ? 'bg-emerald-100 text-emerald-700' :
                          referral.status === 'Telephonic Cleared' ? 'bg-teal-100 text-teal-700' :
                          referral.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                          referral.status === 'Contacted' ? 'bg-blue-100 text-blue-700' :
                          referral.status === 'Applied' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {referral.status}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Progress Steps */}
                    {!isRejected && (
                      <div className="my-3">
                        <div className="flex items-center gap-1 mb-2">
                          {steps.slice(0, 5).map((step, idx) => (
                            <div key={step} className="flex items-center flex-1">
                              <div className={`h-1.5 flex-1 rounded-full ${
                                idx <= currentStepIndex ? 'bg-green-500' : 'bg-gray-200'
                              }`} />
                            </div>
                          ))}
                        </div>
                        {/* Potential Earnings Indicator */}
                        {!isRewarded && (
                          <div className="flex items-center justify-between text-xs">
                            <span className={currentStepIndex >= 4 ? 'text-amber-600 font-semibold' : 'text-gray-500'}>
                              {currentStepIndex >= 4 ? '₹250 Earned!' : `Telephonic + Reg → ₹250`}
                            </span>
                            <span className={currentStepIndex >= 5 ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                              {currentStepIndex >= 5 ? '₹500 Earned!' : `Hired → ₹500`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-gray-400" />
                        {referral.job_title || 'Unknown Position'}
                      </p>
                      <p className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        {referral.company_name || 'Company'}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      Referred on {formatISTDate(referral.created_at)}
                    </p>
                  </CardContent>
                </Card>
              )})}
            </div>
          </div>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <UserPlus className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Referrals Yet</h3>
              <p className="text-gray-500 mb-4">Refer your friends to jobs and earn rewards when they complete registration!</p>
              <Button onClick={() => setActiveSection('jobs')} className="bg-emerald-600 hover:bg-emerald-700">
                <Share2 className="h-4 w-4 mr-2" />
                Browse Jobs to Refer
              </Button>
            </CardContent>
          </Card>
        )}
        
            {/* Reward Structure - Always Visible */}
            <Card className="border-0 shadow-lg mt-6">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-slate-800 mb-4">Reward Structure</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold shadow-lg">
                        ₹250
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Telephonic + Registration</h4>
                        <p className="text-xs text-gray-500">When friend clears telephonic round & completes registration process</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold shadow-lg">
                        ₹500
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Friend Gets Hired</h4>
                        <p className="text-xs text-gray-500">₹500 per candidate when your referred friend gets hired</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Support Section */}
                <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="h-8 w-8 text-blue-500" />
                      <div>
                        <h4 className="font-semibold text-slate-800">Need Help with Referrals?</h4>
                        <p className="text-xs text-gray-500">Contact support for tracking info or any queries</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline"
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 text-white border-0"
                      onClick={() => {
                        setIsSupportOpen(true);
                        toast.info('Ask your referral questions in support');
                      }}
                      data-testid="referral-support-bottom-btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Contact Support
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Earnings History Section - Consolidated from Refer & Earn */}
            {myReferralEarnings.length > 0 && (
              <Card className="border-0 shadow-lg mt-6">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg text-slate-800 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Earnings History
                  </h3>
                  <div className="space-y-3">
                    {myReferralEarnings.map((earning, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{earning.referred_name || 'Referral'}</p>
                            <p className="text-xs text-gray-500">{earning.job_title || 'Position Applied'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">+₹{earning.amount}</p>
                          <p className="text-xs text-gray-400">
                            {earning.earned_at ? formatISTDate(earning.earned_at) : 'Pending'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Skill Assessments Section - Phase 3 */}
        {activeSection === 'assessments' && (
          <div data-testid="assessments-section" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Brain className="h-6 w-6 text-purple-500" />
                Skill Assessments
              </h2>
              <Badge variant="outline" className="bg-purple-50 text-purple-600">
                {assessments.filter(a => a.completed && a.result?.passed).length} Skills Verified
              </Badge>
            </div>

            <p className="text-slate-600">
              Verify your skills through quick assessments. Passed assessments appear as badges on your profile, making you stand out to employers.
            </p>

            {assessments.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Brain className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Assessments Available</h3>
                  <p className="text-gray-500">Check back soon for skill assessments in your domain!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {assessments.map((assessment) => {
                  const isLocked = assessment.is_locked_for_user;
                  return (
                    <Card 
                      key={assessment.id} 
                      data-testid={`assessment-card-${assessment.id}`}
                      className={`border-0 shadow-lg transition-all ${
                        isLocked 
                          ? 'opacity-60 cursor-not-allowed bg-slate-100'
                          : 'hover:shadow-xl cursor-pointer'
                      } ${assessment.completed ? 'border-l-4 border-l-green-500' : ''}`}
                      onClick={() => {
                        if (isLocked) {
                          toast.error(`This assessment has been locked. Contact admin for access.`);
                          return;
                        }
                        if (!assessment.completed) {
                          setSelectedAssessment(assessment);
                        }
                      }}
                    >
                      <CardContent className="p-5">
                        {/* Locked Badge */}
                        {isLocked && (
                          <div className="flex items-center gap-2 mb-3 p-2 bg-orange-100 rounded-lg text-orange-700 text-xs">
                            <Lock className="h-4 w-4" />
                            <span>Locked by {assessment.locked_by_name || 'Admin'}</span>
                          </div>
                        )}
                        
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                              isLocked 
                                ? 'bg-slate-200'
                                : assessment.completed 
                                  ? assessment.result?.passed ? 'bg-green-100' : 'bg-red-100'
                                  : 'bg-purple-100'
                            }`}>
                              {isLocked 
                                ? <Lock className="h-6 w-6 text-slate-500" />
                                : assessment.completed 
                                  ? assessment.result?.passed 
                                    ? <Trophy className="h-6 w-6 text-green-600" />
                                    : <X className="h-6 w-6 text-red-600" />
                                  : <Brain className="h-6 w-6 text-purple-600" />
                              }
                            </div>
                            <div>
                              <h3 className={`font-semibold ${isLocked ? 'text-slate-500' : 'text-slate-800'}`}>{assessment.skill_name}</h3>
                              <p className="text-xs text-slate-500">{assessment.questions?.length || 0} questions</p>
                            </div>
                          </div>
                          {assessment.completed && !isLocked && (
                            <Badge className={assessment.result?.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {assessment.result?.passed ? 'Passed' : 'Failed'}
                            </Badge>
                          )}
                        </div>
                        
                        {assessment.description && (
                          <p className={`text-sm mb-3 line-clamp-2 ${isLocked ? 'text-slate-400' : 'text-slate-600'}`}>{assessment.description}</p>
                        )}

                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {assessment.time_limit_minutes} min
                          </span>
                          <span>{assessment.passing_score}% to pass</span>
                        </div>

                        {!isLocked && assessment.completed ? (
                          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">Your Score</span>
                              <span className={`font-bold ${assessment.result?.passed ? 'text-green-600' : 'text-red-600'}`}>
                                {assessment.result?.score}%
                              </span>
                            </div>
                          </div>
                        ) : !isLocked ? (
                          <Button 
                            className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAssessment(assessment);
                            }}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Start Assessment
                          </Button>
                        ) : (
                          <div className="w-full mt-4 p-3 bg-slate-200 rounded-lg text-center text-slate-500 text-sm">
                            <Lock className="h-4 w-4 inline mr-2" />
                            Access Restricted
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Career Paths Section - Phase 3 */}
        {activeSection === 'career-paths' && (
          <div data-testid="career-paths-section" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Route className="h-6 w-6 text-amber-500" />
                Career Paths
              </h2>
            </div>

            <p className="text-slate-600">
              Explore career progressions and plan your professional growth. See what skills you need and what roles await you.
            </p>

            {/* Recommended Paths */}
            {recommendedPaths.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold text-lg text-slate-800 mb-4 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  Recommended for You
                </h3>
                <div className="grid gap-4 md:grid-cols-3">
                  {recommendedPaths.map((path) => (
                    <Card 
                      key={path.id}
                      data-testid={`recommended-path-${path.id}`}
                      className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-l-amber-500"
                      onClick={() => setSelectedCareerPath(path)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Zap className="h-5 w-5 text-amber-600" />
                          </div>
                          <h4 className="font-semibold text-slate-800">{path.domain}</h4>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">{path.description}</p>
                        <div className="mt-3 flex items-center text-xs text-amber-600">
                          <span>{path.steps?.length || 0} career stages</span>
                          <ArrowRight className="h-3 w-3 ml-auto" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* All Career Paths */}
            <h3 className="font-semibold text-lg text-slate-800 mb-4">All Career Paths</h3>
            {careerPaths.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Route className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Loading Career Paths...</h3>
                  <p className="text-gray-500">Please wait while we fetch career paths for you.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {careerPaths.map((path) => (
                  <Card 
                    key={path.id}
                    data-testid={`career-path-${path.id}`}
                    className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer"
                    onClick={() => setSelectedCareerPath(path)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#0F4C81] to-[#10B981] flex items-center justify-center flex-shrink-0">
                          <Route className="h-7 w-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-slate-800 mb-1">{path.domain}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-3">{path.description}</p>
                          
                          {/* Career Stages Preview */}
                          <div className="flex items-center gap-1 flex-wrap">
                            {path.steps?.slice(0, 4).map((step, idx) => (
                              <React.Fragment key={idx}>
                                <Badge variant="outline" className="text-xs bg-slate-50">
                                  {step.level}
                                </Badge>
                                {idx < Math.min(path.steps.length - 1, 3) && (
                                  <ArrowRight className="h-3 w-3 text-slate-400" />
                                )}
                              </React.Fragment>
                            ))}
                            {path.steps?.length > 4 && (
                              <span className="text-xs text-slate-400">+{path.steps.length - 4} more</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Skills to Develop */}
                      {path.skills_to_develop && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <p className="text-xs text-slate-500 mb-2">Key Skills</p>
                          <div className="flex flex-wrap gap-1">
                            {path.skills_to_develop.slice(0, 5).map((skill, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {path.skills_to_develop.length > 5 && (
                              <Badge variant="secondary" className="text-xs">+{path.skills_to_develop.length - 5}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        <AdSlot slot="dashboard" label="Sponsored" className="mb-2" />
      </main>
      </PullToRefresh>
      </div>

      <InstallPrompt bottomClass="bottom-[76px]" />

      {/* Profile Dialog */}
      <Dialog open={isProfileOpen} onOpenChange={(open) => { setIsProfileOpen(open); if (!open) setIsEditingProfile(false); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                My Profile
              </span>
              {!isEditingProfile && (
                <Button variant="outline" size="sm" onClick={openProfileEdit} data-testid="edit-profile-btn">
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {!isEditingProfile ? (
            // View Mode - Basic Details + Applications
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-3xl shadow-lg">
                  {getGenderEmoji(currentUser?.name)}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{currentUser?.name}</h3>
                  <p className="text-sm text-gray-500">{currentUser?.email}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                {currentUser?.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 p-2 bg-gray-50 rounded-lg">
                    <Phone className="h-4 w-4 text-gray-400" />
                    {currentUser?.phone}
                  </div>
                )}
                {(currentUser?.current_position || currentUser?.current_company) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 p-2 bg-gray-50 rounded-lg">
                    <Briefcase className="h-4 w-4 text-gray-400" />
                    {currentUser?.current_position} {currentUser?.current_company && `at ${currentUser?.current_company}`}
                  </div>
                )}
                {currentUser?.location && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 p-2 bg-gray-50 rounded-lg">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {currentUser?.location}
                  </div>
                )}
              </div>
              
              {/* AI Resume Parser Section */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  AI Resume Parser
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">FREE</Badge>
                </h4>
                
                {currentUser?.resume_parsed ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm text-emerald-700">Resume analyzed</span>
                      </div>
                      <label className="cursor-pointer">
                        <input 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.docx"
                          onChange={handleParseResume}
                          disabled={parsingResume}
                        />
                        <span className="text-xs text-purple-600 hover:underline">
                          {parsingResume ? 'Analyzing...' : 'Upload new'}
                        </span>
                      </label>
                    </div>
                    
                    {/* Skills */}
                    {currentUser?.skills && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">Extracted Skills</p>
                        <div className="flex flex-wrap gap-1">
                          {currentUser.skills.split(',').slice(0, 8).map((skill, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs bg-white">
                              {skill.trim()}
                            </Badge>
                          ))}
                          {currentUser.skills.split(',').length > 8 && (
                            <Badge variant="outline" className="text-xs">+{currentUser.skills.split(',').length - 8} more</Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border-2 border-dashed border-purple-200">
                    <Sparkles className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-700 mb-1">Upload your resume</p>
                    <p className="text-xs text-gray-500 mb-3">AI will extract your skills, experience & education</p>
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        className="hidden" 
                        accept=".pdf,.docx"
                        onChange={handleParseResume}
                        disabled={parsingResume}
                      />
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" disabled={parsingResume}>
                        {parsingResume ? (
                          <>
                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-3 w-3 mr-1" />
                            Upload Resume (PDF/DOCX)
                          </>
                        )}
                      </Button>
                    </label>
                  </div>
                )}
              </div>
              
              {/* My Applications Section */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-blue-600" />
                  My Applications ({myApplications.length})
                </h4>
                {myApplications.length === 0 ? (
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <Briefcase className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No applications yet</p>
                    <p className="text-xs text-gray-400 mt-1">Browse jobs to apply</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {myApplications.map((app) => (
                      <div key={app.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {app.company_name?.charAt(0)?.toUpperCase() || 'J'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{app.position_name || 'Position'}</p>
                            <p className="text-xs text-gray-500 truncate">{app.company_name}</p>
                          </div>
                        </div>
                        <Badge className={`text-[10px] flex-shrink-0 ${
                          app.status === 'Shortlisted' ? 'bg-green-100 text-green-700' :
                          app.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                          app.status === 'Called' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {app.status || 'Applied'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Edit Mode
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name</Label>
                  <Input 
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+91 9876543210"
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input 
                    value={profileForm.location}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="City, Country"
                  />
                </div>
                <div>
                  <Label>Current Position</Label>
                  <Input 
                    value={profileForm.current_position}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, current_position: e.target.value }))}
                    placeholder="Software Engineer"
                  />
                </div>
                <div>
                  <Label>Current Company</Label>
                  <Input 
                    value={profileForm.current_company}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, current_company: e.target.value }))}
                    placeholder="Company Name"
                  />
                </div>
                <div>
                  <Label>Experience (Years)</Label>
                  <Input 
                    type="number"
                    value={profileForm.experience_years}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, experience_years: e.target.value }))}
                    placeholder="5"
                  />
                </div>
                <div>
                  <Label>Education</Label>
                  <Input 
                    value={profileForm.education}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, education: e.target.value }))}
                    placeholder="B.Tech in Computer Science"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Skills (comma separated)</Label>
                  <Input 
                    value={profileForm.skills}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, skills: e.target.value }))}
                    placeholder="React, Node.js, Python, SQL"
                  />
                </div>
                <div>
                  <Label>LinkedIn URL</Label>
                  <Input 
                    value={profileForm.linkedin_url}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <div>
                  <Label>Portfolio URL</Label>
                  <Input 
                    value={profileForm.portfolio_url}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, portfolio_url: e.target.value }))}
                    placeholder="https://yourportfolio.com"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Bio</Label>
                  <Textarea 
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself..."
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsEditingProfile(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-blue-600 hover:bg-blue-700">
                  {savingProfile ? 'Saving...' : <><Save className="h-4 w-4 mr-1" /> Save Profile</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Job Detail Dialog - Enhanced with Company Profile */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  {selectedJob.company_logo_url ? (
                    <img 
                      src={getImageUrl(selectedJob.company_logo_url, API)}
                      alt={selectedJob.company_name}
                      className="h-16 w-16 rounded-xl object-cover border"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-[#0F4C81] to-[#10B981] flex items-center justify-center text-white font-bold text-2xl">
                      {selectedJob.company_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <DialogTitle className="text-xl font-heading">{selectedJob.company_name}</DialogTitle>
                    <p className="text-muted-foreground flex items-center gap-1 mt-1">
                      <Briefcase className="h-4 w-4" />
                      {selectedJob.position_name || 'Multiple Positions Available'}
                    </p>
                    {/* Employment Type Badge */}
                    {selectedJob.employment_type && (
                      <Badge variant="outline" className="mt-2 text-xs">{selectedJob.employment_type}</Badge>
                    )}
                  </div>
                </div>
              </DialogHeader>

              {selectedJob.job_image_url && (
                <div className="my-4">
                  <img 
                    src={getImageUrl(selectedJob.job_image_url, API)}
                    alt="Job"
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                </div>
              )}

              <div className="space-y-5">
                {/* Quick Info Badges */}
                <div className="flex flex-wrap gap-3">
                  {selectedJob.location && (
                    <div className="flex items-center gap-1.5 text-sm bg-slate-100 px-3 py-1.5 rounded-full">
                      <MapPin className="h-3.5 w-3.5 text-slate-500" />
                      <span>{selectedJob.location}</span>
                    </div>
                  )}
                  {selectedJob.work_type && (
                    <div className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full ${
                      selectedJob.work_type === 'Work from Home' ? 'bg-green-100 text-green-700' :
                      selectedJob.work_type === 'Hybrid' ? 'bg-purple-100 text-purple-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {selectedJob.work_type === 'Work from Home' ? <Home className="h-3.5 w-3.5" /> :
                       selectedJob.work_type === 'Hybrid' ? <Laptop className="h-3.5 w-3.5" /> :
                       <Building className="h-3.5 w-3.5" />}
                      <span>{selectedJob.work_type === 'Work from Home' ? 'Remote' : selectedJob.work_type}</span>
                    </div>
                  )}
                  {selectedJob.experience && (
                    <div className="flex items-center gap-1.5 text-sm bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full">
                      <Award className="h-3.5 w-3.5" />
                      <span>{selectedJob.experience}</span>
                    </div>
                  )}
                  {selectedJob.education && (
                    <div className="flex items-center gap-1.5 text-sm bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full">
                      <GraduationCap className="h-3.5 w-3.5" />
                      <span>{selectedJob.education}</span>
                    </div>
                  )}
                </div>

                {/* Salary Information */}
                {selectedJob.salary_range && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <h4 className="font-semibold text-sm text-emerald-800 mb-1 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Salary Range
                    </h4>
                    <p className="text-lg font-bold text-emerald-700">
                      {selectedJob.salary_currency || '₹'} {selectedJob.salary_range}
                      <span className="text-sm font-normal text-emerald-600 ml-2">per annum</span>
                    </p>
                    
                    {/* Salary Insights - Phase 2 */}
                    {salaryInsights[selectedJob.id] && salaryInsights[selectedJob.id].comparison && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm text-emerald-700">
                            Market Comparison: <strong>{salaryInsights[selectedJob.id].comparison.comparison}</strong>
                          </span>
                          {salaryInsights[selectedJob.id].comparison.indicator === 'high' && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">+{salaryInsights[selectedJob.id].comparison.percentage_diff}%</Badge>
                          )}
                          {salaryInsights[selectedJob.id].comparison.indicator === 'low' && (
                            <Badge className="bg-red-100 text-red-700 text-xs">{salaryInsights[selectedJob.id].comparison.percentage_diff}%</Badge>
                          )}
                        </div>
                        <p className="text-xs text-emerald-600 mt-1">
                          Market range for {salaryInsights[selectedJob.id].market_insights.experience_level} level: {salaryInsights[selectedJob.id].market_insights.formatted_range}
                        </p>
                      </div>
                    )}
                    {!salaryInsights[selectedJob.id] && (
                      <button
                        onClick={() => fetchSalaryInsights(selectedJob.id)}
                        className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                      >
                        <BarChart3 className="h-3 w-3" />
                        View Market Comparison
                      </button>
                    )}
                  </div>
                )}

                {/* Job Match Score - Phase 2 */}
                {selectedJob.required_skills && selectedJob.required_skills.length > 0 && currentUser?.skills && (
                  <div data-testid="job-match-section" className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm text-purple-800 flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Your Match Score
                      </h4>
                      {jobMatchScores[selectedJob.id] && (
                        <span className={`text-2xl font-bold ${
                          jobMatchScores[selectedJob.id].score >= 70 ? 'text-emerald-600' :
                          jobMatchScores[selectedJob.id].score >= 40 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {jobMatchScores[selectedJob.id].score}%
                        </span>
                      )}
                    </div>
                    
                    {jobMatchScores[selectedJob.id] ? (
                      <div className="space-y-3">
                        {/* Skill Match Breakdown */}
                        <div>
                          <div className="flex items-center justify-between text-xs text-purple-600 mb-1">
                            <span>Skills Match</span>
                            <span>{jobMatchScores[selectedJob.id].skill_match_percent}%</span>
                          </div>
                          <div className="h-2 bg-purple-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-600 rounded-full transition-all duration-500"
                              style={{ width: `${jobMatchScores[selectedJob.id].skill_match_percent}%` }}
                            />
                          </div>
                        </div>
                        
                        {/* Matched Skills */}
                        {jobMatchScores[selectedJob.id].matched_skills.length > 0 && (
                          <div>
                            <p className="text-xs text-purple-600 mb-1">Matched Skills</p>
                            <div className="flex flex-wrap gap-1">
                              {jobMatchScores[selectedJob.id].matched_skills.map((skill, idx) => (
                                <Badge key={idx} className="bg-emerald-100 text-emerald-700 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Missing Skills */}
                        {jobMatchScores[selectedJob.id].missing_skills.length > 0 && (
                          <div>
                            <p className="text-xs text-purple-600 mb-1">Skills to Develop</p>
                            <div className="flex flex-wrap gap-1">
                              {jobMatchScores[selectedJob.id].missing_skills.map((skill, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-purple-300 text-purple-600">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Experience Match */}
                        <div className="flex items-center gap-2 pt-2 border-t border-purple-200">
                          <span className="text-xs text-purple-600">Experience:</span>
                          <Badge className={`text-xs ${
                            jobMatchScores[selectedJob.id].experience_match === 'Excellent' ? 'bg-emerald-100 text-emerald-700' :
                            jobMatchScores[selectedJob.id].experience_match === 'Good' ? 'bg-blue-100 text-blue-700' :
                            jobMatchScores[selectedJob.id].experience_match === 'Overqualified' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {jobMatchScores[selectedJob.id].experience_match}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchJobMatchScore(selectedJob.id)}
                        className="w-full border-purple-300 text-purple-600 hover:bg-purple-100"
                      >
                        <Target className="h-4 w-4 mr-2" />
                        Calculate My Match
                      </Button>
                    )}
                  </div>
                )}

                {/* Company Profile Section */}
                {selectedJob.company_profile && (
                  <div data-testid="company-profile-section" className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-4">
                    <h4 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#0F4C81]" />
                      About {selectedJob.company_name}
                    </h4>
                    {selectedJob.company_profile.about && (
                      <p className="text-sm text-slate-600 mb-3">{selectedJob.company_profile.about}</p>
                    )}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedJob.company_profile.industry && (
                        <div>
                          <span className="text-slate-500">Industry:</span>
                          <span className="ml-2 font-medium">{selectedJob.company_profile.industry}</span>
                        </div>
                      )}
                      {selectedJob.company_profile.company_size && (
                        <div>
                          <span className="text-slate-500">Company Size:</span>
                          <span className="ml-2 font-medium">{selectedJob.company_profile.company_size} employees</span>
                        </div>
                      )}
                      {selectedJob.company_profile.headquarters && (
                        <div>
                          <span className="text-slate-500">Headquarters:</span>
                          <span className="ml-2 font-medium">{selectedJob.company_profile.headquarters}</span>
                        </div>
                      )}
                      {selectedJob.company_profile.founded_year && (
                        <div>
                          <span className="text-slate-500">Founded:</span>
                          <span className="ml-2 font-medium">{selectedJob.company_profile.founded_year}</span>
                        </div>
                      )}
                    </div>
                    {selectedJob.company_profile.culture && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Culture</span>
                        <p className="text-sm text-slate-600 mt-1">{selectedJob.company_profile.culture}</p>
                      </div>
                    )}
                    {selectedJob.company_profile.benefits && selectedJob.company_profile.benefits.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Perks & Benefits</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedJob.company_profile.benefits.map((benefit, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs bg-white">
                              {benefit}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Required Skills */}
                {selectedJob.required_skills && selectedJob.required_skills.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      Required Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedJob.required_skills.map((skill, idx) => (
                        <Badge key={idx} className="bg-slate-100 text-slate-700 hover:bg-slate-200">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Structured Job Description */}
                {selectedJob.structured_description ? (
                  <div data-testid="structured-description-section" className="space-y-4">
                    {selectedJob.structured_description.overview && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Overview</h4>
                        <p className="text-sm text-muted-foreground">{selectedJob.structured_description.overview}</p>
                      </div>
                    )}
                    {selectedJob.structured_description.responsibilities && selectedJob.structured_description.responsibilities.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Key Responsibilities</h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {selectedJob.structured_description.responsibilities.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedJob.structured_description.qualifications && selectedJob.structured_description.qualifications.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Qualifications</h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {selectedJob.structured_description.qualifications.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {selectedJob.structured_description.nice_to_have && selectedJob.structured_description.nice_to_have.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Nice to Have</h4>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {selectedJob.structured_description.nice_to_have.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Lightbulb className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Fallback to original description format */
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Job Description</h4>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {decoratedText}
                      </a>
                    )}>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.job_description}</p>
                    </Linkify>
                  </div>
                )}

                {selectedJob.requirements && !selectedJob.structured_description && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Requirements</h4>
                    <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                      <a href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {decoratedText}
                      </a>
                    )}>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedJob.requirements}</p>
                    </Linkify>
                  </div>
                )}

                {selectedJob.career_site_link && (
                  <a 
                    href={selectedJob.career_site_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#0F4C81] hover:text-[#0F4C81]/80 font-medium"
                  >
                    <Link2 className="h-4 w-4" />
                    View on Company Career Site
                  </a>
                )}

                <div className="pt-4 border-t">
                  {myApplications.some(app => app.job_id === selectedJob.id) ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-base py-2 px-4">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      You have already applied
                    </Badge>
                  ) : (
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => { setSelectedJob(null); handleApplyClick(selectedJob); }}
                        className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Apply for this Position
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => { setSelectedJob(null); handleReferClick(selectedJob); }}
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Refer Someone
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Form Dialog */}
      <Dialog open={isApplyDialogOpen} onOpenChange={setIsApplyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-violet-600" />
              Apply to {applyingToJob?.company_name}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{applyingToJob?.position_name || 'Multiple Positions Available'}</p>
          </DialogHeader>
          
          {/* Daily Application Limit Notice - Only show if limit is close */}
          {dailyApplicationCount.remaining <= 1 && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="text-sm">
                <span className="font-medium text-amber-800">Note: </span>
                <span className="text-amber-700">
                  {dailyApplicationCount.remaining === 0 
                    ? "You have reached your daily application limit."
                    : "This is your last application for today."}
                </span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmitApplication} className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input 
                  id="full_name"
                  value={applicationForm.full_name}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, full_name: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email"
                  type="email"
                  value={applicationForm.email}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="aadhar_address">Address as per Aadhar Card *</Label>
              <Textarea 
                id="aadhar_address"
                value={applicationForm.aadhar_address}
                onChange={(e) => setApplicationForm(prev => ({ ...prev, aadhar_address: e.target.value }))}
                placeholder="Enter your complete address as per Aadhar card"
                rows={2}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input 
                  id="phone"
                  value={applicationForm.phone}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 XXXXX XXXXX"
                  required
                />
              </div>
              <div>
                <Label htmlFor="education">Education *</Label>
                <Select 
                  value={applicationForm.education} 
                  onValueChange={(value) => setApplicationForm(prev => ({ ...prev, education: value, education_other: value === 'Others' ? prev.education_other : '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Education" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Graduate">Graduate</SelectItem>
                    <SelectItem value="Masters">Masters</SelectItem>
                    <SelectItem value="MBA">MBA</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {applicationForm.education === 'Others' && (
              <div>
                <Label htmlFor="education_other">Please specify your education</Label>
                <Input 
                  id="education_other"
                  value={applicationForm.education_other}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, education_other: e.target.value }))}
                  placeholder="e.g. Diploma, PhD, etc."
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="university_name">University Name</Label>
                <Input 
                  id="university_name"
                  value={applicationForm.university_name}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, university_name: e.target.value }))}
                  placeholder="Enter university name"
                />
              </div>
              <div>
                <Label htmlFor="college_name">College Name</Label>
                <Input 
                  id="college_name"
                  value={applicationForm.college_name}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, college_name: e.target.value }))}
                  placeholder="Enter college name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="current_city_state">Current City/State *</Label>
                <Input 
                  id="current_city_state"
                  value={applicationForm.current_city_state}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, current_city_state: e.target.value }))}
                  placeholder="e.g. Mumbai, Maharashtra"
                  required
                />
              </div>
              <div>
                <Label htmlFor="aadhar_phone_linked">Phone/Family Number linked with Aadhar? *</Label>
                <Select 
                  value={applicationForm.aadhar_phone_linked} 
                  onValueChange={(value) => setApplicationForm(prev => ({ ...prev, aadhar_phone_linked: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="total_experience">Total Experience *</Label>
                <Select 
                  value={applicationForm.total_experience} 
                  onValueChange={(value) => setApplicationForm(prev => ({ ...prev, total_experience: value, previous_company_name: value === 'N/A' ? '' : prev.previous_company_name }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Experience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N/A">N/A (Fresher)</SelectItem>
                    <SelectItem value="0-6 Months">0-6 Months</SelectItem>
                    <SelectItem value="1-2 years">1-2 years</SelectItem>
                    <SelectItem value="2-3 years">2-3 years</SelectItem>
                    <SelectItem value="3-4 years">3-4 years</SelectItem>
                    <SelectItem value="4+ years">4+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="previous_lpa">Previous LPA</Label>
                <Input 
                  id="previous_lpa"
                  value={applicationForm.previous_lpa}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, previous_lpa: e.target.value }))}
                  placeholder="e.g. 5 LPA"
                />
              </div>
            </div>

            {applicationForm.total_experience && applicationForm.total_experience !== 'N/A' && (
              <div>
                <Label htmlFor="previous_company_name">Previous Company Name *</Label>
                <Input 
                  id="previous_company_name"
                  value={applicationForm.previous_company_name}
                  onChange={(e) => setApplicationForm(prev => ({ ...prev, previous_company_name: e.target.value }))}
                  placeholder="Enter your previous company name"
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hear_about_us">How did you hear about us? *</Label>
                <Select 
                  value={applicationForm.hear_about_us} 
                  onValueChange={(value) => setApplicationForm(prev => ({ ...prev, hear_about_us: value, friend_name: value === 'Friend' ? prev.friend_name : '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Social Media">Social Media</SelectItem>
                    <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                    <SelectItem value="Facebook">Facebook</SelectItem>
                    <SelectItem value="Instagram">Instagram</SelectItem>
                    <SelectItem value="Friend">Friend</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {applicationForm.hear_about_us === 'Friend' && (
                <div>
                  <Label htmlFor="friend_name">Friend's Name *</Label>
                  <Input 
                    id="friend_name"
                    value={applicationForm.friend_name}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, friend_name: e.target.value }))}
                    placeholder="Enter your friend's name"
                    required={applicationForm.hear_about_us === 'Friend'}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="followed_instagram">Have you followed us on Instagram? *</Label>
              <Select 
                value={applicationForm.followed_instagram} 
                onValueChange={(value) => setApplicationForm(prev => ({ ...prev, followed_instagram: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {applicationForm.followed_instagram === 'No' && (
                <div className="mt-2 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-200">
                  <p className="text-sm text-pink-700">
                    Please follow us on Instagram for other job updates: 
                    <a 
                      href="https://www.instagram.com/officium_connect?igsh=MW83YnlyMTF3cW5zdA==" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-1 font-medium text-pink-600 hover:text-pink-800 underline"
                    >
                      @officium_connect
                    </a>
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label>Upload CV/Resume *</Label>
              <div className="mt-2">
                {applicationForm.resume_url ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700">CV uploaded successfully</span>
                    <button 
                      type="button"
                      onClick={() => setApplicationForm(prev => ({ ...prev, resume_url: '' }))}
                      className="ml-auto text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-violet-400 transition-colors">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">
                        {uploadingResume ? 'Uploading...' : 'Click to upload CV (PDF, DOC)'}
                      </span>
                    </div>
                    <input 
                      type="file" 
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={handleResumeUpload}
                      disabled={uploadingResume}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Agreement Checkbox */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="agrees_to_charges"
                  checked={applicationForm.agrees_to_charges}
                  onCheckedChange={(checked) => setApplicationForm(prev => ({ ...prev, agrees_to_charges: checked }))}
                />
                <div>
                  <Label htmlFor="agrees_to_charges" className="text-sm font-medium text-amber-900 cursor-pointer">
                    I agree to the terms and conditions *
                  </Label>
                  <p className="text-xs text-amber-700 mt-1">
                    I understand that there may be processing charges associated with this job application, 
                    and I agree to be informed about any applicable fees before they are charged.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsApplyDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
                <Send className="h-4 w-4 mr-2" />
                Submit Application
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-6 w-6" />
              Application Submitted Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {lastAppliedJob && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-700 mb-2">
                  Your application for <strong>{lastAppliedJob.position_name}</strong> at <strong>{lastAppliedJob.company_name}</strong> has been submitted.
                </p>
                <p className="text-xs text-gray-500">
                  A confirmation email has been sent to your registered email address.
                </p>
              </div>
            )}
            
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800 mb-1">Helpful Tip</p>
                  <p className="text-sm text-amber-700">
                    After applying, carefully review the job description and prepare a few relevant questions. This will help you clear the telephonic round successfully.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowSuccessDialog(false)} className="bg-green-600 hover:bg-green-700">
              Got it, Thanks!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skill Assessment Dialog - Phase 3 */}
      <Dialog open={!!selectedAssessment && !isAssessmentActive} onOpenChange={(open) => { if (!open) setSelectedAssessment(null); }}>
        <DialogContent className="max-w-lg">
          {selectedAssessment && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-600" />
                  {selectedAssessment.skill_name} Assessment
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {selectedAssessment.description && (
                  <p className="text-slate-600">{selectedAssessment.description}</p>
                )}
                
                <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Questions</span>
                    <span className="font-semibold">{selectedAssessment.questions?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Time Limit</span>
                    <span className="font-semibold">{selectedAssessment.time_limit_minutes} minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Passing Score</span>
                    <span className="font-semibold">{selectedAssessment.passing_score}%</span>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Once you start, you cannot pause or retake this assessment. Make sure you have enough time and a stable connection.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setSelectedAssessment(null)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      setIsAssessmentActive(true);
                      setAssessmentStartTime(Date.now());
                      setAssessmentAnswers({});
                      // Initialize timer with assessment time limit
                      setAssessmentTimeLeft(selectedAssessment.time_limit_minutes * 60);
                    }}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Assessment
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Active Assessment Dialog - Phase 3 */}
      <Dialog open={isAssessmentActive} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" onPointerDownOutside={(e) => e.preventDefault()}>
          {selectedAssessment && (
            <>
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-purple-600" />
                    {selectedAssessment.skill_name} Assessment
                  </span>
                  <div className="flex items-center gap-3">
                    {/* Timer Display */}
                    {assessmentTimeLeft !== null && (
                      <Badge 
                        variant="outline" 
                        className={`${
                          assessmentTimeLeft <= 60 
                            ? 'bg-red-100 text-red-700 border-red-300 animate-pulse' 
                            : assessmentTimeLeft <= 300 
                              ? 'bg-orange-100 text-orange-700 border-orange-300'
                              : 'bg-green-100 text-green-700 border-green-300'
                        } font-mono text-sm px-3`}
                      >
                        <Clock className="h-4 w-4 mr-1" />
                        {Math.floor(assessmentTimeLeft / 60)}:{String(assessmentTimeLeft % 60).padStart(2, '0')}
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-purple-50 text-purple-600">
                      {Object.keys(assessmentAnswers).length}/{selectedAssessment.questions?.length || 0} answered
                    </Badge>
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Question Navigation */}
              <div className="flex flex-wrap gap-2 py-3 border-b border-slate-100 flex-shrink-0">
                {selectedAssessment.questions?.map((q, idx) => (
                  <button
                    key={q.id}
                    onClick={() => {
                      const el = document.getElementById(`question-${q.id}`);
                      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                      assessmentAnswers[q.id] !== undefined
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 250px)' }}>
                <div className="space-y-6 py-4">
                  {selectedAssessment.questions?.map((question, qIdx) => (
                    <div key={question.id} id={`question-${question.id}`} className={`rounded-xl p-4 ${question.question_type === 'theoretical' ? 'bg-amber-50' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <p className="font-medium text-slate-800">
                          <span className="text-purple-600 mr-2">Q{qIdx + 1}.</span>
                          {question.question}
                        </p>
                        {question.question_type === 'theoretical' && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 text-xs">
                            Theoretical ({question.max_marks || 5} marks)
                          </Badge>
                        )}
                      </div>
                      
                      {/* MCQ Options */}
                      {question.question_type !== 'theoretical' && question.options?.length > 0 && (
                        <div className="space-y-2">
                          {question.options.map((option, oIdx) => (
                            <button
                              key={oIdx}
                              onClick={() => setAssessmentAnswers(prev => ({ ...prev, [question.id]: oIdx }))}
                              className={`w-full text-left p-3 rounded-lg border transition-all ${
                                assessmentAnswers[question.id] === oIdx
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-slate-200 hover:border-purple-300 hover:bg-white'
                              }`}
                            >
                              <span className="font-medium mr-2">{String.fromCharCode(65 + oIdx)}.</span>
                              {option}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Theoretical Answer Text Area */}
                      {question.question_type === 'theoretical' && (
                        <div className="space-y-2">
                          <Textarea
                            value={assessmentAnswers[question.id] || ''}
                            onChange={(e) => setAssessmentAnswers(prev => ({ ...prev, [question.id]: e.target.value }))}
                            placeholder="Write your answer here..."
                            rows={5}
                            className="bg-white border-amber-200 focus:border-amber-400"
                          />
                          <p className="text-xs text-amber-600">
                            {(assessmentAnswers[question.id] || '').length} characters
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t flex-shrink-0">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (window.confirm('Are you sure you want to quit? Your progress will be lost.')) {
                      setIsAssessmentActive(false);
                      setSelectedAssessment(null);
                      setAssessmentAnswers({});
                      setAssessmentTimeLeft(null);
                    }
                  }}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Quit
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={submitAssessment}
                  disabled={Object.keys(assessmentAnswers).length !== selectedAssessment.questions?.length}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Submit Assessment ({Object.keys(assessmentAnswers).length}/{selectedAssessment.questions?.length})
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Career Path Detail Dialog - Phase 3 */}
      <Dialog open={!!selectedCareerPath} onOpenChange={(open) => { if (!open) setSelectedCareerPath(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedCareerPath && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Route className="h-5 w-5 text-amber-500" />
                  {selectedCareerPath.domain}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                <p className="text-slate-600">{selectedCareerPath.description}</p>

                {/* Career Progression Timeline */}
                <div>
                  <h4 className="font-semibold text-slate-800 mb-4">Career Progression</h4>
                  <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#0F4C81] to-[#10B981]" />
                    
                    <div className="space-y-6">
                      {selectedCareerPath.steps?.map((step, idx) => (
                        <div key={idx} className="relative flex gap-4">
                          {/* Timeline Node */}
                          <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            idx === 0 ? 'bg-[#0F4C81]' : 
                            idx === selectedCareerPath.steps.length - 1 ? 'bg-[#10B981]' :
                            'bg-gradient-to-br from-[#0F4C81] to-[#10B981]'
                          }`}>
                            <span className="text-white font-bold text-sm">{idx + 1}</span>
                          </div>
                          
                          {/* Step Content */}
                          <Card className="flex-1 border-0 shadow-md">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h5 className="font-semibold text-slate-800">{step.role}</h5>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="text-xs">{step.level}</Badge>
                                    <span className="text-xs text-slate-500">{step.typical_years}</span>
                                  </div>
                                </div>
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                  {step.avg_salary_range}
                                </Badge>
                              </div>
                              
                              {step.description && (
                                <p className="text-sm text-slate-600 mb-3">{step.description}</p>
                              )}
                              
                              {step.skills_needed && (
                                <div className="flex flex-wrap gap-1">
                                  {step.skills_needed.map((skill, sIdx) => (
                                    <Badge key={sIdx} variant="secondary" className="text-xs">
                                      {skill}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Skills to Develop */}
                {selectedCareerPath.skills_to_develop && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3">Skills to Develop</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCareerPath.skills_to_develop.map((skill, idx) => (
                        <Badge key={idx} className="bg-purple-100 text-purple-700">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommended Certifications */}
                {selectedCareerPath.certifications_recommended && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3">Recommended Certifications</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCareerPath.certifications_recommended.map((cert, idx) => (
                        <Badge key={idx} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <Award className="h-3 w-3 mr-1" />
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Support Dialog */}
      <Dialog open={isSupportOpen} onOpenChange={(open) => { setIsSupportOpen(open); if (!open) { setSelectedTicket(null); setSupportTab('tickets'); } }}>
        <DialogContent className="max-w-2xl w-[95vw] h-[85vh] md:h-auto md:max-h-[85vh] overflow-hidden flex flex-col p-0 rounded-xl">
          <DialogHeader className="p-4 pb-3 border-b bg-gradient-to-r from-violet-600 to-purple-700">
            <DialogTitle className="flex items-center gap-2 text-white">
              <HelpCircle className="h-5 w-5" />
              Support Center
            </DialogTitle>
          </DialogHeader>
          
          {/* Tab Navigation */}
          <div className="flex border-b bg-gray-50">
            <button
              onClick={() => { setSupportTab('tickets'); setSelectedTicket(null); }}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                supportTab === 'tickets' 
                  ? 'text-violet-700 border-b-2 border-violet-600 bg-white' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MessageCircle className="h-4 w-4" />
              My Tickets
            </button>
            <button
              onClick={() => { setSupportTab('call-request'); setSelectedTicket(null); }}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                supportTab === 'call-request' 
                  ? 'text-emerald-700 border-b-2 border-emerald-600 bg-white' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Phone className="h-4 w-4" />
              Request a Call
            </button>
          </div>

          {/* Call Request Tab */}
          {supportTab === 'call-request' && (
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Request a Callback</h3>
                  <p className="text-sm text-gray-500 mt-1">Our support team will call you during your preferred time</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-amber-800 flex items-center gap-2">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span><strong>Support Hours:</strong> Mon-Sat: 10AM-10PM | Sunday: 10AM-4PM</span>
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                  <p className="text-xs text-blue-800 flex items-center gap-2">
                    <Globe className="h-4 w-4 flex-shrink-0" />
                    <span>You can raise your request in <strong>any language</strong> - Hindi, English, or your native language</span>
                  </p>
                </div>

                <form onSubmit={handleSubmitCallRequest} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Phone Number *</Label>
                    <Input
                      type="tel"
                      placeholder="Enter your phone number"
                      value={callRequestForm.phone}
                      onChange={(e) => setCallRequestForm({ ...callRequestForm, phone: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Preferred Date *</Label>
                    <Input
                      type="date"
                      min={getMinDate()}
                      value={callRequestForm.preferred_date}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        // Reset time slot when date changes (in case switching to/from Sunday)
                        setCallRequestForm({ ...callRequestForm, preferred_date: selectedDate, preferred_time: '' });
                      }}
                      className="mt-1"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">All days available. Sunday: Half day (10AM-4PM)</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Preferred Time Slot *</Label>
                    <Select
                      value={callRequestForm.preferred_time}
                      onValueChange={(value) => setCallRequestForm({ ...callRequestForm, preferred_time: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableTimeSlots().map((slot) => (
                          <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isSunday(callRequestForm.preferred_date) && (
                      <p className="text-xs text-amber-600 mt-1">Sunday hours: 10AM - 4PM only</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Reason for Call *</Label>
                    <Textarea
                      placeholder="आप हिंदी, English या अपनी भाषा में लिख सकते हैं..."
                      value={callRequestForm.reason}
                      onChange={(e) => setCallRequestForm({ ...callRequestForm, reason: e.target.value })}
                      className="mt-1 min-h-[100px]"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Write in any language you're comfortable with</p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
                    disabled={isSubmittingCallRequest}
                  >
                    {isSubmittingCallRequest ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Phone className="h-4 w-4 mr-2" />
                        Submit Call Request
                      </>
                    )}
                  </Button>
                </form>

                {/* Show existing call requests */}
                {supportTickets.filter(t => t.ticket_type === 'call_request').length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Call Requests</h4>
                    <div className="space-y-2">
                      {supportTickets.filter(t => t.ticket_type === 'call_request').map((ticket) => (
                        <div key={ticket.id} className="p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">{ticket.preferred_date} at {ticket.preferred_time}</p>
                              <p className="text-xs text-gray-500 mt-1">{ticket.message}</p>
                            </div>
                            <Badge className={`text-[10px] ${
                              ticket.call_status === 'Pending' ? 'bg-orange-100 text-orange-700' :
                              ticket.call_status === 'Called' ? 'bg-blue-100 text-blue-700' :
                              ticket.call_status === 'Completed' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {ticket.call_status || 'Pending'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share Support Link */}
                <div className="mt-6 pt-6 border-t">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Share Support Link
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">Share this link with others to help them reach our support team directly.</p>
                  <div className="flex gap-2">
                    <Input 
                      value={`${window.location.origin}/support`}
                      readOnly
                      className="flex-1 text-sm bg-gray-50"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/support`);
                        toast.success('Support link copied!');
                      }}
                      className="flex-shrink-0"
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tickets Tab */}
          {supportTab === 'tickets' && (
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Tickets List - Full width on mobile, fixed width on desktop */}
            <div className={`w-full md:w-72 border-r flex flex-col ${selectedTicket ? 'hidden md:flex' : ''}`}>
              <div className="flex items-center justify-between p-3 border-b bg-gray-50 flex-shrink-0">
                <h4 className="text-sm font-semibold">My Tickets</h4>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setSelectedTicket('new')}
                  className="h-7 text-xs md:hidden bg-violet-500 text-white border-0 hover:bg-violet-600"
                >
                  <MessageCircle className="h-3 w-3 mr-1" />
                  New
                </Button>
              </div>
              <ScrollArea className="flex-1 min-h-0">
                {supportTickets.filter(t => t.ticket_type !== 'call_request').length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">No tickets yet</p>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setSelectedTicket('new')}
                      className="mt-4"
                    >
                      Create Your First Ticket
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {supportTickets.filter(t => t.ticket_type !== 'call_request').map((ticket) => (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedTicket?.id === ticket.id 
                            ? 'bg-violet-100 border border-violet-300' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <p className="font-medium text-sm truncate">{ticket.subject}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-[10px] ${getTicketStatusColor(ticket.status)}`}>
                            {ticket.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {ticket.replies?.length || 0} replies
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Ticket Detail / New Ticket Form - Full width on mobile when selected/new */}
            <div className={`flex-1 flex flex-col overflow-hidden ${!selectedTicket ? 'hidden md:flex' : 'w-full md:w-2/3'}`}>
              {selectedTicket && selectedTicket !== 'new' ? (
                <>
                  {/* Ticket Header - Clean Style */}
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-4 py-3 flex items-center gap-3">
                    <button 
                      onClick={() => setSelectedTicket(null)}
                      className="md:hidden p-1 hover:bg-white/20 rounded-full transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Headphones className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{selectedTicket.subject}</h4>
                      <p className="text-xs text-violet-200">
                        {selectedTicket.status === 'Open' ? 'Waiting for response' : 
                         selectedTicket.status === 'In Progress' ? 'Support is responding...' : 
                         selectedTicket.status}
                      </p>
                    </div>
                    <Badge className={`text-[10px] ${
                      selectedTicket.status === 'Open' ? 'bg-yellow-400 text-yellow-900' :
                      selectedTicket.status === 'In Progress' ? 'bg-violet-300 text-violet-900' :
                      'bg-gray-300 text-gray-700'
                    }`}>
                      {selectedTicket.status}
                    </Badge>
                  </div>

                  {/* Messages - Clean Light Style */}
                  <ScrollArea className="flex-1 pr-2 bg-gray-50">
                    <div className="space-y-2 p-3 min-h-full">
                      {/* Original message - Your first message */}
                      <div className="flex justify-end">
                        <div className="max-w-[85%] bg-violet-100 rounded-lg rounded-tr-none p-2 px-3 shadow-sm">
                          <p className="text-sm text-gray-800">
                            <LinkifyText text={selectedTicket.message} />
                          </p>
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-[10px] text-gray-500">
                              {new Date(selectedTicket.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <svg className="w-3.5 h-3.5 text-violet-500" viewBox="0 0 16 15" fill="currentColor">
                              <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Replies */}
                      {selectedTicket.replies?.map((reply, index) => {
                        const isMe = reply.sender_email === currentUser?.email;
                        const isSending = reply.is_sending || reply.status === 'sending';
                        const isFailed = reply.status === 'failed';
                        
                        return (
                          <div 
                            key={reply.id} 
                            className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-200`}
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <div 
                              className={`max-w-[85%] rounded-lg p-2 px-3 shadow-sm ${
                                isMe 
                                  ? 'bg-violet-100 rounded-tr-none' 
                                  : 'bg-white rounded-tl-none'
                              } ${isSending ? 'opacity-70' : ''} ${isFailed ? 'bg-red-100' : ''}`}
                            >
                              {!isMe && (
                                <p className="text-xs font-semibold text-violet-600 mb-1">
                                  {reply.sender_name} (Support)
                                </p>
                              )}
                              <p className="text-sm text-gray-800">
                                <LinkifyText text={reply.message} />
                              </p>
                              <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-[10px] text-gray-500">
                                  {isSending ? 'Sending...' : new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {isMe && (
                                  isSending ? (
                                    <svg className="w-3.5 h-3.5 text-gray-400 animate-pulse" viewBox="0 0 16 15" fill="currentColor">
                                      <path d="M9.75 7.5a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0z"/>
                                    </svg>
                                  ) : isFailed ? (
                                    <svg className="w-3.5 h-3.5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                                    </svg>
                                  ) : (
                                    <svg className="w-3.5 h-3.5 text-violet-500" viewBox="0 0 16 15" fill="currentColor">
                                      <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.89 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/>
                                    </svg>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Reply Input - Clean Style */}
                  {selectedTicket.status !== 'Closed' && (
                    <div className="p-3 border-t bg-white flex gap-2 items-center">
                      <EmojiPicker 
                        onEmojiSelect={(emoji) => setReplyMessage(prev => prev + emoji)}
                        disabled={isSendingReply}
                      />
                      <Input
                        placeholder="Type a message..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !isSendingReply && handleSendReply()}
                        disabled={isSendingReply}
                        className="flex-1 rounded-full bg-gray-50 border-gray-200 shadow-sm focus-visible:ring-1 focus-visible:ring-violet-500"
                        data-testid="message-input"
                      />
                      <Button 
                        onClick={handleSendReply} 
                        className="rounded-full w-10 h-10 p-0 bg-violet-500 hover:bg-violet-600 shadow-md transition-transform active:scale-95"
                        disabled={isSendingReply || !replyMessage.trim()}
                        data-testid="send-message-btn"
                      >
                        {isSendingReply ? (
                          <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                /* New Ticket Form - Shows for 'new' state or on desktop when null */
                <ScrollArea className="flex-1">
                <form onSubmit={handleCreateTicket} className="space-y-4 p-4">
                  {/* Back button on mobile */}
                  {selectedTicket === 'new' && (
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedTicket(null)}
                      className="md:hidden -ml-2 mb-2"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back to tickets
                    </Button>
                  )}
                  <div>
                    <h4 className="font-semibold mb-3">Create New Support Ticket</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Need help? Send us a message and our team will respond as soon as possible.
                    </p>
                  </div>

                  {/* Support Hours Info */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                      <Clock className="h-4 w-4 flex-shrink-0" />
                      <span><strong>Support Hours:</strong> Mon-Sat: 10AM-10PM | Sunday: 10AM-4PM</span>
                    </p>
                  </div>

                  {/* Language Support Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800 flex items-center gap-2">
                      <Globe className="h-4 w-4 flex-shrink-0" />
                      <span>You can raise your request in <strong>any language</strong> - Hindi, English, or your native language</span>
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="ticket-subject">Subject *</Label>
                    <Input
                      id="ticket-subject"
                      value={newTicket.subject}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="What do you need help with?"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="ticket-message">Message *</Label>
                    <Textarea
                      id="ticket-message"
                      value={newTicket.message}
                      onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="आप हिंदी, English या अपनी भाषा में लिख सकते हैं..."
                      rows={5}
                      className="mt-1"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Write in any language you're comfortable with</p>
                  </div>

                  <Button type="submit" className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
                    <Send className="h-4 w-4 mr-2" />
                    Submit Ticket
                  </Button>
                </form>
                </ScrollArea>
              )}
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refer Someone Dialog */}
      <Dialog open={isReferDialogOpen} onOpenChange={setIsReferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Refer Someone
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Refer a friend or colleague for: <span className="font-medium">{referringToJob?.company_name}</span>
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmitReferral} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="referred_name">Full Name *</Label>
              <Input
                id="referred_name"
                value={referralForm.referred_name}
                onChange={(e) => setReferralForm(prev => ({ ...prev, referred_name: e.target.value }))}
                placeholder="Enter their full name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="referred_email">Email *</Label>
              <Input
                id="referred_email"
                type="email"
                value={referralForm.referred_email}
                onChange={(e) => setReferralForm(prev => ({ ...prev, referred_email: e.target.value }))}
                placeholder="Enter their email"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="referred_phone">Phone Number</Label>
              <Input
                id="referred_phone"
                value={referralForm.referred_phone}
                onChange={(e) => setReferralForm(prev => ({ ...prev, referred_phone: e.target.value }))}
                placeholder="Enter their phone number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="relationship">Relationship</Label>
              <Select 
                value={referralForm.relationship} 
                onValueChange={(value) => setReferralForm(prev => ({ ...prev, relationship: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="How do you know them?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Colleague">Colleague</SelectItem>
                  <SelectItem value="Friend">Friend</SelectItem>
                  <SelectItem value="Family">Family</SelectItem>
                  <SelectItem value="Former Coworker">Former Coworker</SelectItem>
                  <SelectItem value="Classmate">Classmate</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={referralForm.notes}
                onChange={(e) => setReferralForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Why do you think they'd be a good fit?"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsReferDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                <UserPlus className="h-4 w-4 mr-2" />
                Submit Referral
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" />
              Withdraw Application
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                Warning
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Are you sure you want to withdraw your application for:
              </p>
              <p className="text-amber-900 font-semibold mt-2">
                {withdrawingAppName}
              </p>
            </div>
            <p className="text-gray-500 text-sm">
              This action cannot be undone. You will need to apply again if you change your mind.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setWithdrawDialogOpen(false);
                setWithdrawingApplicationId(null);
                setWithdrawingAppName('');
              }}
              className="px-6"
              data-testid="withdraw-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmWithdraw}
              className="px-6 bg-red-600 hover:bg-red-700"
              data-testid="withdraw-confirm-btn"
            >
              Yes, Withdraw
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Position Request Dialog */}
      <Dialog open={isPositionRequestOpen} onOpenChange={setIsPositionRequestOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="position-request-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Request for a Position
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Request an update about a position or a new company listing
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmitPositionRequest} className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pos_candidate_name">Your Name *</Label>
                <Input
                  id="pos_candidate_name"
                  value={positionRequestForm.candidate_name}
                  onChange={(e) => setPositionRequestForm(prev => ({ ...prev, candidate_name: e.target.value }))}
                  placeholder="Enter your full name"
                  required
                  data-testid="position-candidate-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pos_company_name">Company Name *</Label>
                <Input
                  id="pos_company_name"
                  value={positionRequestForm.company_name}
                  onChange={(e) => setPositionRequestForm(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="e.g., Google, Amazon"
                  required
                  data-testid="position-company-name"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pos_position_name">Position Name *</Label>
                <Input
                  id="pos_position_name"
                  value={positionRequestForm.position_name}
                  onChange={(e) => setPositionRequestForm(prev => ({ ...prev, position_name: e.target.value }))}
                  placeholder="e.g., Software Engineer"
                  required
                  data-testid="position-position-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pos_company_link">Company / Job Posting Link</Label>
                <Input
                  id="pos_company_link"
                  type="url"
                  value={positionRequestForm.company_link}
                  onChange={(e) => setPositionRequestForm(prev => ({ ...prev, company_link: e.target.value }))}
                  placeholder="https://..."
                  data-testid="position-company-link"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pos_cv">Upload CV (PDF, DOC - Max 5MB)</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="pos_cv"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleCVUpload}
                  className="flex-1"
                  disabled={uploadingCV}
                  data-testid="position-cv-upload"
                />
                {uploadingCV && <span className="text-sm text-muted-foreground">Uploading...</span>}
                {positionRequestForm.cv_url && (
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> CV Uploaded
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pos_reason">Reason for Request *</Label>
              <Select 
                value={positionRequestForm.reason} 
                onValueChange={(value) => setPositionRequestForm(prev => ({ ...prev, reason: value }))}
                required
              >
                <SelectTrigger data-testid="position-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New Position Request">New Position Request</SelectItem>
                  <SelectItem value="Update on Existing Position">Update on Existing Position</SelectItem>
                  <SelectItem value="Request for New Company">Request for New Company</SelectItem>
                  <SelectItem value="Job Not Listed">Job Not Listed on Platform</SelectItem>
                  <SelectItem value="Career Guidance">Career Guidance</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pos_notes">Additional Notes</Label>
              <Textarea
                id="pos_notes"
                value={positionRequestForm.notes}
                onChange={(e) => setPositionRequestForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional details about your request..."
                rows={3}
                data-testid="position-notes"
              />
            </div>

            {/* Info Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Our team will review your request and you will receive an update via email within <strong>one week</strong>.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPositionRequestOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                data-testid="submit-position-request"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </form>

          {/* My Previous Requests */}
          {myPositionRequests.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold text-lg mb-3">My Previous Requests</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {myPositionRequests.map(req => (
                  <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{req.position_name} at {req.company_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatIST(req.created_at, 'MMM d, yyyy')}
                      </p>
                    </div>
                    <Badge className={getPositionRequestStatusColor(req.status)}>
                      {req.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Promotional Popup Announcement Modal - Robot Mascot Style */}
      <Dialog open={showPopupModal} onOpenChange={setShowPopupModal}>
        <DialogContent className="max-w-sm p-0 overflow-visible border-0 bg-transparent shadow-none" data-testid="popup-announcement-modal">
          {activePopup && (
            <div className="relative flex flex-col items-center">
              {/* Popup Animation Styles */}
              <style>{`
                @keyframes card-float {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-5px); }
                }
                @keyframes card-entrance {
                  0% { transform: scale(0.8) translateY(20px); opacity: 0; }
                  100% { transform: scale(1) translateY(0); opacity: 1; }
                }
                @keyframes sparkle {
                  0%, 100% { opacity: 0; transform: scale(0); }
                  50% { opacity: 1; transform: scale(1); }
                }
                .card-animated {
                  animation: card-entrance 0.4s ease-out forwards, card-float 3s ease-in-out 0.4s infinite;
                }
                .sparkle-1 { animation: sparkle 2s ease-in-out infinite; animation-delay: 0s; }
                .sparkle-2 { animation: sparkle 2s ease-in-out infinite; animation-delay: 0.5s; }
                .sparkle-3 { animation: sparkle 2s ease-in-out infinite; animation-delay: 1s; }
              `}</style>
              
              {/* Announcement Card - Above Mascot */}
              <div className="relative w-full mb-[-40px] z-10 card-animated">
                {/* Sparkle decorations */}
                <div className="absolute -top-3 -left-3 w-4 h-4 text-yellow-300 sparkle-1">✨</div>
                <div className="absolute -top-2 right-8 w-3 h-3 text-yellow-200 sparkle-2">✨</div>
                <div className="absolute top-12 -right-2 w-3 h-3 text-orange-200 sparkle-3">✨</div>
                
                {/* Close button */}
                <button
                  onClick={dismissPopup}
                  className="absolute -top-2 -right-2 z-20 bg-white hover:bg-gray-100 text-gray-500 rounded-full p-2 shadow-lg transition-all hover:scale-110"
                >
                  <X className="h-4 w-4" />
                </button>

                {/* Card with gradient and wavy pattern */}
                <div className="bg-gradient-to-br from-amber-300 via-orange-400 to-rose-400 rounded-3xl shadow-2xl p-6 pb-16 overflow-hidden relative">
                  {/* Decorative wavy lines */}
                  <div className="absolute inset-0 opacity-30">
                    <svg className="w-full h-full" viewBox="0 0 400 300" preserveAspectRatio="none">
                      <path d="M0,100 Q100,50 200,100 T400,100" stroke="rgba(255,255,255,0.4)" strokeWidth="3" fill="none"/>
                      <path d="M0,150 Q100,100 200,150 T400,150" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none"/>
                      <path d="M0,200 Q100,150 200,200 T400,200" stroke="rgba(255,255,255,0.2)" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="relative text-center">
                    {/* Title with lightning bolt */}
                    <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 rounded-full mb-4 shadow-lg">
                      <span className="text-orange-500 text-lg">⚡</span>
                      <span className="text-orange-600 font-bold text-lg">{activePopup.title}</span>
                    </div>

                    {/* Discount Badge */}
                    {activePopup.content.includes('%') && (
                      <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl font-bold text-2xl shadow-lg mb-4">
                        {activePopup.content.match(/\d+%/)?.[0] || '50%'} Off
                      </div>
                    )}

                    {/* Content (if no % discount) */}
                    {!activePopup.content.includes('%') && (
                      <p className="text-white font-semibold text-lg mb-4 drop-shadow">
                        {activePopup.content}
                      </p>
                    )}

                    {/* End Date */}
                    <div className="flex items-center justify-center gap-3 text-orange-800/80">
                      <span className="w-2 h-2 rounded-full bg-orange-600/50"></span>
                      <span className="font-medium">
                        Ends {activePopup.end_date 
                          ? new Date(activePopup.end_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
                          : 'Soon'}
                      </span>
                      <span className="w-2 h-2 rounded-full bg-orange-600/50"></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Mascot - At Bottom, Based on Selection - With Animation */}
              <div className="relative z-20 -mt-2">
                <style>{`
                  /* Base entrance animation */
                  .mascot-entrance {
                    animation: mascot-pop-in 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
                  }
                  @keyframes mascot-pop-in {
                    0% { transform: scale(0) rotate(-10deg); opacity: 0; }
                    60% { transform: scale(1.1) rotate(5deg); }
                    100% { transform: scale(1) rotate(0deg); opacity: 1; }
                  }
                  
                  /* Glow effect for all mascots */
                  .mascot-glow {
                    filter: drop-shadow(0 0 12px rgba(251, 146, 60, 0.5));
                  }
                  
                  /* Robot animations - mechanical bounce */
                  .mascot-robot {
                    animation: robot-bounce 1.5s ease-in-out infinite, robot-glow 2s ease-in-out infinite;
                  }
                  @keyframes robot-bounce {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    25% { transform: translateY(-6px) rotate(-2deg); }
                    75% { transform: translateY(-6px) rotate(2deg); }
                  }
                  @keyframes robot-glow {
                    0%, 100% { filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.5)); }
                    50% { filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.8)); }
                  }
                  
                  /* Cat animations - stretch and purr */
                  .mascot-cat {
                    animation: cat-stretch 3s ease-in-out infinite;
                  }
                  @keyframes cat-stretch {
                    0%, 100% { transform: scaleY(1) translateY(0); }
                    30% { transform: scaleY(1.05) translateY(-3px); }
                    60% { transform: scaleY(0.98) translateY(2px); }
                  }
                  
                  /* Dog animations - excited tail wag */
                  .mascot-dog {
                    animation: dog-wag 0.5s ease-in-out infinite, dog-bounce 1s ease-in-out infinite;
                  }
                  @keyframes dog-wag {
                    0%, 100% { transform: rotate(-3deg); }
                    50% { transform: rotate(3deg); }
                  }
                  @keyframes dog-bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                  }
                  
                  /* Bear animations - sleepy sway */
                  .mascot-bear {
                    animation: bear-sway 4s ease-in-out infinite;
                  }
                  @keyframes bear-sway {
                    0%, 100% { transform: rotate(-2deg) translateX(0); }
                    50% { transform: rotate(2deg) translateX(3px); }
                  }
                  
                  /* Owl animations - wise head tilt */
                  .mascot-owl {
                    animation: owl-tilt 3s ease-in-out infinite, owl-blink 4s ease-in-out infinite;
                  }
                  @keyframes owl-tilt {
                    0%, 100% { transform: rotate(0deg); }
                    30% { transform: rotate(-8deg); }
                    70% { transform: rotate(8deg); }
                  }
                  @keyframes owl-blink {
                    0%, 45%, 55%, 100% { opacity: 1; }
                    50% { opacity: 0.8; }
                  }
                  
                  /* Fox animations - sneaky slide */
                  .mascot-fox {
                    animation: fox-sneak 2.5s ease-in-out infinite;
                  }
                  @keyframes fox-sneak {
                    0%, 100% { transform: translateX(0) rotate(0deg); }
                    25% { transform: translateX(-5px) rotate(-3deg); }
                    75% { transform: translateX(5px) rotate(3deg); }
                  }
                  
                  /* Penguin animations - waddle */
                  .mascot-penguin {
                    animation: penguin-waddle 1s ease-in-out infinite;
                  }
                  @keyframes penguin-waddle {
                    0%, 100% { transform: rotate(-5deg) translateX(-2px); }
                    50% { transform: rotate(5deg) translateX(2px); }
                  }
                  
                  /* Bunny animations - hop */
                  .mascot-bunny {
                    animation: bunny-hop 1.2s ease-in-out infinite;
                  }
                  @keyframes bunny-hop {
                    0%, 100% { transform: translateY(0) scaleY(1); }
                    30% { transform: translateY(0) scaleY(0.9); }
                    50% { transform: translateY(-12px) scaleY(1.05); }
                    70% { transform: translateY(0) scaleY(0.95); }
                  }
                  
                  /* Panda animations - lazy roll */
                  .mascot-panda {
                    animation: panda-roll 4s ease-in-out infinite;
                  }
                  @keyframes panda-roll {
                    0%, 100% { transform: rotate(0deg); }
                    25% { transform: rotate(-5deg); }
                    75% { transform: rotate(5deg); }
                  }
                  
                  /* Elephant animations - trunk sway */
                  .mascot-elephant {
                    animation: elephant-sway 3s ease-in-out infinite;
                  }
                  @keyframes elephant-sway {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    50% { transform: translateY(-4px) rotate(2deg); }
                  }
                  
                  /* Lion animations - majestic shake */
                  .mascot-lion {
                    animation: lion-shake 3s ease-in-out infinite;
                  }
                  @keyframes lion-shake {
                    0%, 100% { transform: scale(1) rotate(0deg); }
                    25% { transform: scale(1.02) rotate(-1deg); }
                    50% { transform: scale(1.03) rotate(0deg); }
                    75% { transform: scale(1.02) rotate(1deg); }
                  }
                  
                  /* Koala animations - sleepy nod */
                  .mascot-koala {
                    animation: koala-nod 4s ease-in-out infinite;
                  }
                  @keyframes koala-nod {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    30% { transform: translateY(3px) rotate(-3deg); }
                    70% { transform: translateY(3px) rotate(3deg); }
                  }
                  
                  /* Monkey animations - playful swing */
                  .mascot-monkey {
                    animation: monkey-swing 1.5s ease-in-out infinite;
                  }
                  @keyframes monkey-swing {
                    0%, 100% { transform: rotate(-8deg); }
                    50% { transform: rotate(8deg); }
                  }
                  
                  /* Robot V2 animations - tech pulse */
                  .mascot-robot_v2 {
                    animation: robot-v2-pulse 2s ease-in-out infinite;
                  }
                  @keyframes robot-v2-pulse {
                    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 10px rgba(59, 130, 246, 0.5)); }
                    50% { transform: scale(1.03); filter: drop-shadow(0 0 25px rgba(59, 130, 246, 0.9)); }
                  }
                  
                  /* Robot V3 animations - heartbeat */
                  .mascot-robot_v3 {
                    animation: robot-v3-heartbeat 1.5s ease-in-out infinite;
                  }
                  @keyframes robot-v3-heartbeat {
                    0%, 100% { transform: scale(1); }
                    15% { transform: scale(1.05); }
                    30% { transform: scale(1); }
                    45% { transform: scale(1.03); }
                  }
                `}</style>
                <div className={`mascot-entrance mascot-${activePopup.mascot_type || 'robot'}`}>
                  {(() => {
                    const mascot = getMascotById(activePopup.mascot_type || 'robot');
                    const MascotComponent = mascot.component;
                    return <MascotComponent />;
                  })()}
                </div>
              </div>

              {/* Action Buttons - Below Mascot */}
              <div className="flex gap-3 mt-4 w-full px-4">
                <style>{`
                  @keyframes button-pulse {
                    0%, 100% { box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4); }
                    50% { box-shadow: 0 4px 25px rgba(249, 115, 22, 0.7); }
                  }
                  .cta-button-animated {
                    animation: button-pulse 2s ease-in-out infinite;
                  }
                  .cta-button-animated:hover {
                    animation: none;
                    transform: scale(1.02);
                  }
                `}</style>
                {activePopup.button_link && (
                  <a
                    href={activePopup.button_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-button-animated flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-bold text-center transition-all"
                  >
                    {activePopup.button_text || 'Claim Now'}
                  </a>
                )}
                <button
                  onClick={dismissPopup}
                  className="flex-1 bg-white hover:bg-gray-50 text-gray-600 px-6 py-3 rounded-xl font-medium transition-all shadow-lg border border-gray-200"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Learn More Modal - Referral Eligibility Details */}
      <Dialog open={isLearnMoreOpen} onOpenChange={setIsLearnMoreOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Gift className="h-6 w-6 text-emerald-600" />
              How to be Eligible for Rewards
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Step by Step Guide */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700 text-sm flex-shrink-0">1</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Refer a Friend to Jobs</h4>
                  <p className="text-sm text-gray-500">Share job opportunities with your friends and network</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700 text-sm flex-shrink-0">2</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Friend Applies for Position</h4>
                  <p className="text-sm text-gray-500">Your referred friend applies for the job position</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center font-bold text-white text-sm flex-shrink-0">3</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Telephonic Cleared + Registration Complete</h4>
                  <p className="text-sm text-gray-500">When your friend clears the telephonic round and completes the registration process</p>
                  <p className="text-amber-600 font-bold mt-1">You earn ₹250!</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center font-bold text-white text-sm flex-shrink-0">4</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Friend Gets Hired</h4>
                  <p className="text-sm text-gray-500">When your referred friend successfully gets hired</p>
                  <p className="text-green-600 font-bold mt-1">You earn ₹500 per candidate!</p>
                </div>
              </div>
            </div>

            {/* Tracking Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-gray-800">Track Your Referrals</h4>
              </div>
              <p className="text-sm text-gray-600">You can track all your referrals' progress in real-time. Check status or contact support for any queries about your referrals.</p>
            </div>

            {/* Support Link */}
            <div className="flex justify-between items-center pt-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsLearnMoreOpen(false);
                  setIsSupportOpen(true);
                  toast.info('Ask your referral questions in support');
                }}
                className="flex items-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                Contact Support
              </Button>
              <Button 
                onClick={() => setIsLearnMoreOpen(false)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Got it!
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation Bar - Hidden on desktop */}
      <BottomNav
        active={isProfileOpen ? 'profile' : isSupportOpen ? 'support' : activeSection}
        onSelect={setActiveSection}
        items={[
          { id: 'jobs', label: 'Jobs', icon: Briefcase },
          { id: 'applications', label: 'Applied', icon: FileText, badge: myApplications.length },
          { id: 'support', label: 'Support', icon: MessageCircle, onClick: () => setIsSupportOpen(true),
            badge: supportTickets.filter(t => t.status !== 'Closed').length },
          { id: 'profile', label: 'Profile', icon: User, onClick: () => setIsProfileOpen(true) },
          { id: 'menu', label: 'Menu', icon: Menu, onClick: () => setSidebarCollapsed(false) },
        ]}
      />

      {/* Floating Support Button - Only visible on desktop now */}
      <button
        onClick={() => setIsSupportOpen(true)}
        className="fixed bottom-6 right-6 z-40 hidden sm:flex bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4 rounded-full shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-110"
        data-testid="floating-support-btn"
        aria-label="Contact Support"
      >
        <MessageCircle className="h-6 w-6" />
        {supportTickets.filter(t => t.status !== 'Closed').length > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs bg-red-500 text-white rounded-full font-bold">
            {supportTickets.filter(t => t.status !== 'Closed').length}
          </span>
        )}
      </button>
    </div>
  );
};

export default CandidateDashboard;
